import { formatDisplayDate } from "@/lib/format";
import { useState, useEffect, useLayoutEffect, useRef, useMemo, lazy, Suspense, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTenantPath } from "@/hooks/useTenantPath";
import { ChartTooltip } from "@/components/ui/ChartTooltip";
import { useHoverDismiss } from "@/components/ui/useHoverDismiss";
import { MultiSeriesChart, ChartCardHeader, paletteColor, CHART_SEMANTIC, type ChartSeries } from "@/components/charts";
import { HoverTip } from "@/components/ui/HoverTip";
import { PageHeader } from "@/components/ui/PageHeader";
import { Icon } from "@/components/ui/Icon";
import { UpdatedAgo } from "@/components/ui/UpdatedAgo";
import type { MonthlyBucket } from "@/hooks/useMonthlyShipments";
import { useMonthlySales, type MonthlySalesBucket } from "@/hooks/useMonthlySales";
import { useMonthlyReceipts, type MonthlyReceiptBucket } from "@/hooks/useMonthlyReceipts";
import { useSalesBalancesSummary, type SalesBalancesSummary } from "@/hooks/useSalesBalancesSummary";
import { useSalesAllocationsSummary, type SalesAllocationsSummary } from "@/hooks/useSalesAllocationsSummary";
import { useVehicles } from "@/hooks/useVehicles";
import { useUsers } from "@/hooks/useUsers";
import { useWorkdayConfig } from "@/hooks/useWorkdayConfig";
import { isWorkday, nonWorkdayReason } from "@/domain/workday";
import { useAuth, useShipmentMixPerm } from "@/hooks/useAuth";
import { useRawMaterials } from "@/hooks/useRawMaterials";
import { useMaterialCategories } from "@/hooks/useMaterialCategories";
import { classifyTier, type RqcShipment } from "@/domain/rqc";
import type { MaterialCategory, MaterialCategoryKind, RawMaterialPrice } from "@/domain/material";
import { getMatUnit } from "@/pages/Material/lib";
import { fmt, summarize } from "@/pages/Dispatch/lib";
import { ScheduleCard } from "@/pages/Dispatch/ScheduleCard";
import { classifyStatus, type ShipmentStatus } from "@/pages/Dispatch/filterLogic";
import { useSiteMeta } from "@/hooks/useSiteMeta";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { TodayStatusCardV2View } from "@/pages/Dispatch/TodayStatusCardV2View";
import { useHomeMonthly, useHomeShipKind, useHomeShipMonth, useHomeToday } from "@/hooks/useHomeShipments";
import { useAsconSchedule } from "@/pages/Dispatch/ascon/useAsconSchedule";
import { useConfirm } from "@/hooks/useConfirm";
import { useDashboardPrefs } from "@/hooks/useDashboardPrefs";
import { useSpace } from "@/hooks/useSpace";
import { defaultBiz, homeBoardKey, tenantHasBiz } from "@/domain/space";
import { CAL_WEEKDAYS, buildMonthCells, calYmd, type CalCell } from "@/domain/calendarGrid";
import type { DashboardWidget } from "@/services/dashboard_prefs.repository";
import { HOME_WIDGETS as AVAILABLE_WIDGETS, canShowWidget, kpiWidgetId, widgetDef, type HomeWidgetType } from "@/domain/home_widgets";
import { useRoleHome } from "@/hooks/useRoleHome";
import type { RoleUnit } from "@/services/roles.repository";
import { RoleLayoutModal } from "@/pages/DashboardRoleLayout";
import {
  WidgetSettingsModal,
  buildLineChartTaxonomy,
  buildShipmentTaxonomy,
  periodSlice,
  type WidgetSettings,
} from "@/pages/DashboardWidgetSettings";
import {
  DotModeContext,
  useDotMode,
  DEFAULT_DOT_MODE,
  DOT_MODE_STORAGE_KEY,
  type DotModeState,
  type DotScope,
} from "@/components/charts/dot-mode-context";

// KPI[1] 당월 매출 / KPI[2] 평균 단가 — sales_invoices(ERP 미러) 실데이터 wire 완료 (2026-06-16).
//   매출 = Σ합계금액(amount+vat, PeriodView와 동일) · 평균단가 = Σ공급가/Σ수량 (공급가/㎥).
//   useMonthlySales(year) 로 월별 집계. quotes 단가 derive 방식은 폐기(영업 데이터가 source of truth).

// ─────────────────────────────────────────────────────────────
// Wire 상태 (2026-06-16):
//   ✅ KPI[0] 당월 출하량 / 월별 출하 bar 차트  (useMonthlyShipments)
//   ✅ KPI[1] 당월 매출                          (useMonthlySales — sales_invoices Σ합계금액)
//   ✅ KPI[2] 평균 단가                          (useMonthlySales — Σ공급가/Σ수량)
//   ✅ KPI[3] 가동 차량                          (useVehicles)
//   ✅ 금일 출하 테이블                          (useRqcShipments(kstToday()))
//   ✅ 원재료비 line 차트                        (useRawMaterials + useMaterialCategories)
// ─────────────────────────────────────────────────────────────
// ── KPI 빌더 공통 헬퍼 ─────────────────────────────────────────
// settings(WidgetSettings) 를 받아 차원/비교기준/기간/sparkline 표시를 반영한다.
// prev_year 비교는 호출부가 전년도 monthly 데이터를 주입해야 한다(없으면 prev_month 폴백).

/** 현재 월(KST, 1~12) */
function curMonth(): number {
  return new Date().getMonth() + 1;
}

/** delta(%) + 방향 계산 — cur vs base */
function calcDelta(cur: number, base: number): { delta: number; up: boolean } {
  const delta = base > 0 ? ((cur - base) / base) * 100 : cur > 0 ? 100 : 0;
  return { delta, up: delta >= 0 };
}

/** "+8.2%" 형태 라벨 */
function deltaLabel(delta: number, up: boolean): string {
  return `${up ? "+" : ""}${delta.toFixed(1)}%`;
}

/** period 에 맞춰 spark 배열을 슬라이스. kpiShowSpark===false 면 빈 배열 */
function sparkSlice<T>(arr: T[], s: WidgetSettings | undefined): T[] {
  if (s?.kpiShowSpark === false) return [];
  const { startIdx, endIdx } = periodSlice(s?.period ?? "year");
  return arr.slice(startIdx, endIdx + 1);
}

/** 비교 기준값 추출: prev_month 면 직전월, prev_year 면 전년 동월(데이터 있을 때) */
function compareBaseValue(
  curMonthly: number[],
  prevYearMonthly: number[] | undefined,
  s: WidgetSettings | undefined,
): number {
  const cm = curMonth();
  if (s?.kpiCompareBasis === "prev_year" && prevYearMonthly) {
    return prevYearMonthly[cm - 1] ?? 0;
  }
  return cm > 1 ? (curMonthly[cm - 2] ?? 0) : 0;
}

/** 비교기준 sub 라벨 ("전월 대비" / "전년 동월 대비") */
function compareSubLabel(s: WidgetSettings | undefined): string {
  return s?.kpiCompareBasis === "prev_year" ? "전년 동월 대비" : "전월 대비";
}

function buildKpiFleet(
  vehicles: { active: boolean; category: string }[],
  settings?: WidgetSettings,
): typeof KPI_DATA[number] {
  const dim = settings?.kpiDimension ?? "all";
  const activeRows = vehicles.filter((v) => v.active);
  const total = vehicles.length;
  const own = activeRows.filter((v) => v.category === "자차").length;
  const lease = activeRows.filter((v) => v.category === "지입차").length;
  const outsourced = activeRows.filter((v) => v.category === "용차").length;
  // 차원 — all/own/lease/outsourced
  const active =
    dim === "own" ? own :
    dim === "lease" ? lease :
    dim === "outsourced" ? outsourced :
    activeRows.length;
  const dimLabel =
    dim === "own" ? "자차" : dim === "lease" ? "지입차" : dim === "outsourced" ? "용차" : "가동 차량";
  const activePct = total > 0 ? (active / total) * 100 : 0;
  const target = settings?.kpiTarget ?? 0;
  const sub =
    total <= 0 ? "차량 데이터 없음" :
    target > 0 ? `목표 ${target}대 대비 ${target > 0 ? ((active / target) * 100).toFixed(0) : "—"}%` :
    `자차 ${own} · 지입 ${lease} · 용차 ${outsourced}`;
  const sparkFull = total > 0 ? Array(12).fill(active) : [];
  return {
    id: "kpi-fleet",
    label: dim === "all" ? "가동 차량" : `가동 차량 (${dimLabel})`,
    value: String(active),
    unit: `/ ${total}대`,
    delta: `${activePct.toFixed(1)}%`,
    up: true,
    sub,
    spark: sparkSlice(sparkFull, settings),
    sparkLabels: sparkSlice(["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"], settings),
    sparkUnit: "대",
  };
}

// 가동차량 KPI 전용 — 배터리 모양 "칸(cell)" 비중 바.
// 자차/지입/용차 가동분 + 비가동분을 칸 수로 표현. 비율 유지하며 최소 10칸,
// 작은 대수는 짝수배로 키움 (예: 자차3·지입2·용차1 → ×2 → 6·4·2 = 12칸).
function FleetCompositionBar({ vehicles }: { vehicles: { active: boolean; category: string }[] }) {
  const total = vehicles.length;
  const activeRows = vehicles.filter((v) => v.active);
  const own = activeRows.filter((v) => v.category === "자차").length;
  const lease = activeRows.filter((v) => v.category === "지입차").length;
  const outsourced = activeRows.length - own - lease; // 자차·지입차 외는 전부 용차
  const inactive = total - activeRows.length;
  // 최소 10칸이 되도록 짝수배(×2,×4…)로 스케일 — 작은 대수도 칸으로 또렷이.
  let mult = 1;
  while (total > 0 && total * mult < 10) mult *= 2;
  const groups = [
    { key: "own", label: "자차", n: own },
    { key: "lease", label: "지입", n: lease },
    { key: "out", label: "용차", n: outsourced },
    { key: "empty", label: "비가동", n: inactive },
  ];
  const cells: { key: string; label: string }[] = [];
  for (const g of groups) {
    for (let i = 0; i < g.n * mult; i++) cells.push({ key: g.key, label: g.label });
  }
  if (total === 0) {
    return <div className="fleet-batt-wrap"><div className="fleet-batt is-empty" /></div>;
  }
  return (
    <div className="fleet-batt-wrap" title={`가동 ${activeRows.length} / 전체 ${total}대${mult > 1 ? ` · 칸당 ${(1 / mult).toFixed(mult > 2 ? 2 : 1)}대` : ""}`}>
      <div className="fleet-batt">
        {cells.map((c, i) => (
          <span key={i} className={`fleet-cell is-${c.key}`} title={c.label} />
        ))}
      </div>
    </div>
  );
}

function buildKpiShipment(
  monthly: MonthlyBucket[],
  settings?: WidgetSettings,
  prevYear?: MonthlyBucket[],
  unit = "㎥",
): typeof KPI_DATA[number] {
  const dim = settings?.kpiDimension ?? "total";
  const pick = (m: MonthlyBucket | undefined): number =>
    dim === "gov" ? (m?.govM3 ?? 0) : dim === "cust" ? (m?.custM3 ?? 0) : (m?.totalM3 ?? 0);
  const series = monthly.map(pick);
  const prevSeries = prevYear?.map(pick);
  const cm = curMonth();
  const cur = series[cm - 1] ?? 0;
  const base = compareBaseValue(series, prevSeries, settings);
  const { delta, up } = calcDelta(cur, base);
  const dimLabel = dim === "gov" ? "관급" : dim === "cust" ? "사급" : "";
  return {
    id: "kpi-ship",
    label: dim === "total" ? "당월 출하량" : `당월 출하량 (${dimLabel})`,
    value: Math.round(cur).toLocaleString("ko-KR"),
    unit,
    delta: deltaLabel(delta, up),
    up,
    sub: dim === "total"
      ? `관급 ${Math.round(monthly[cm - 1]?.govM3 ?? 0).toLocaleString("ko-KR")} · 사급 ${Math.round(monthly[cm - 1]?.custM3 ?? 0).toLocaleString("ko-KR")}`
      : "",
    spark: sparkSlice(series.map((v) => Math.round(v / 1000 * 10) / 10), settings),
    sparkLabels: sparkSlice(monthly.map((m) => `${m.month}월`), settings),
    sparkUnit: `천${unit}`,
  };
}

function buildKpiRevenue(
  monthly: MonthlySalesBucket[],
  settings?: WidgetSettings,
  prevYear?: MonthlySalesBucket[],
): typeof KPI_DATA[number] {
  const dim = settings?.kpiDimension ?? "total";
  const includeVat = settings?.kpiRevIncludeVat !== false; // 기본 true(포함)
  const pick = (m: MonthlySalesBucket | undefined): number => {
    if (!m) return 0;
    if (dim === "gov") return includeVat ? m.govRevenue : m.govSupply;
    if (dim === "cust") return includeVat ? m.custRevenue : m.custSupply;
    return includeVat ? m.revenue : m.supply;
  };
  const series = monthly.map(pick);
  const prevSeries = prevYear?.map(pick);
  const cm = curMonth();
  const cur = series[cm - 1] ?? 0;
  const base = compareBaseValue(series, prevSeries, settings);
  const { delta, up } = calcDelta(cur, base);
  const dimLabel = dim === "gov" ? "관급" : dim === "cust" ? "사급" : "";
  return {
    id: "kpi-rev",
    label: dim === "total" ? "당월 매출" : `당월 매출 (${dimLabel})`,
    value: (cur / 1e8).toFixed(2),
    unit: "억원",
    delta: deltaLabel(delta, up),
    up,
    sub: includeVat ? "VAT 포함" : "공급가",
    spark: sparkSlice(series.map((v) => Math.round((v / 1e8) * 100) / 100), settings),
    sparkLabels: sparkSlice(monthly.map((m) => `${m.month}월`), settings),
    sparkUnit: "억",
  };
}

function buildKpiPrice(
  monthly: MonthlySalesBucket[],
  settings?: WidgetSettings,
  prevYear?: MonthlySalesBucket[],
): typeof KPI_DATA[number] {
  const dim = settings?.kpiDimension ?? "total";
  const spec = (settings?.kpiPriceSpec ?? "").trim();
  const pick = (m: MonthlySalesBucket | undefined): number => {
    if (spec) {
      const sa = m?.bySpec?.[spec];
      if (!sa) return 0;
      const sup = dim === "gov" ? sa.govSupply : dim === "cust" ? sa.custSupply : sa.supply;
      const q = dim === "gov" ? sa.govQty : dim === "cust" ? sa.custQty : sa.qty;
      return q > 0 ? Math.round(sup / q) : 0;
    }
    return dim === "gov" ? (m?.govAvgPrice ?? 0) : dim === "cust" ? (m?.custAvgPrice ?? 0) : (m?.avgPrice ?? 0);
  };
  const series = monthly.map(pick);
  const prevSeries = prevYear?.map(pick);
  const cm = curMonth();
  const cur = series[cm - 1] ?? 0;
  const base = compareBaseValue(series, prevSeries, settings);
  const { delta, up } = calcDelta(cur, base);
  const dimLabel = dim === "gov" ? "관급" : dim === "cust" ? "사급" : "";
  // 관급/사급·규격은 제목 대신 sub 라인으로 (제목 길어짐/줄바꿈 방지)
  const qualifier = [dimLabel, spec].filter(Boolean).join(" · ");
  const target = settings?.kpiTarget ?? 0;
  const targetText = target > 0
    ? `목표 ${target.toLocaleString("ko-KR")}원 대비 ${cur > 0 ? ((cur / target) * 100).toFixed(0) : "—"}%`
    : "";
  const sub = [qualifier, targetText].filter(Boolean).join("  ·  ");
  return {
    id: "kpi-price",
    label: "평균 단가",
    value: Math.round(cur).toLocaleString("ko-KR"),
    unit: "원/㎥",
    delta: deltaLabel(delta, up),
    up,
    sub,
    spark: sparkSlice(series.map((v) => Math.round((v / 1000) * 10) / 10), settings),
    sparkLabels: sparkSlice(monthly.map((m) => `${m.month}월`), settings),
    sparkUnit: "천원",
  };
}

/** 규격코드("25-21-150") 숫자 파싱 정렬 — 파싱 불가 시 사전순 */
function compareSpec(a: string, b: string): number {
  const pa = a.split("-").map((x) => parseInt(x, 10));
  const pb = b.split("-").map((x) => parseInt(x, 10));
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const va = pa[i];
    const vb = pb[i];
    if (Number.isNaN(va) || Number.isNaN(vb)) return a.localeCompare(b);
    if (va !== vb) return va - vb;
  }
  return a.localeCompare(b);
}

/** 평균단가 규격 선택지 — 월별 버킷 bySpec 합집합 중 수량>0 규격만, 정렬 */
function collectPriceSpecs(monthly: MonthlySalesBucket[]): string[] {
  const totalQty = new Map<string, number>();
  for (const b of monthly) {
    for (const [spec, sa] of Object.entries(b.bySpec ?? {})) {
      totalQty.set(spec, (totalQty.get(spec) ?? 0) + sa.qty);
    }
  }
  return [...totalQty.entries()]
    .filter(([, q]) => q > 0)
    .map(([spec]) => spec)
    .sort(compareSpec);
}

/** 당월 수금 KPI — 당월 Σ수금액(억원) + 전월/전년 비교 + sparkline */
function buildKpiReceipts(
  monthly: MonthlyReceiptBucket[],
  settings?: WidgetSettings,
  prevYear?: MonthlyReceiptBucket[],
): typeof KPI_DATA[number] {
  const series = monthly.map((m) => m.amount);
  const prevSeries = prevYear?.map((m) => m.amount);
  const cm = curMonth();
  const cur = series[cm - 1] ?? 0;
  const base = compareBaseValue(series, prevSeries, settings);
  const { delta, up } = calcDelta(cur, base);
  return {
    id: "kpi-receipt",
    label: "당월 수금",
    value: (cur / 1e8).toFixed(2),
    unit: "억원",
    delta: deltaLabel(delta, up),
    up,
    sub: "",
    spark: sparkSlice(series.map((v) => Math.round((v / 1e8) * 100) / 100), settings),
    sparkLabels: sparkSlice(monthly.map((m) => `${m.month}월`), settings),
    sparkUnit: "억",
  };
}

/** 미수 잔액 KPI — 총 미수 Σ.ar(억원). 스냅샷이라 delta 비활성, sub=기준일 */
function buildKpiAr(
  summary: SalesBalancesSummary,
  _settings?: WidgetSettings,
): typeof KPI_DATA[number] {
  void _settings;
  const cur = summary.totalAr;
  return {
    id: "kpi-ar",
    label: "미수 잔액",
    value: (cur / 1e8).toFixed(2),
    unit: "억원",
    delta: `${summary.count}곳`,
    up: false, // 미수는 낮을수록 좋음 — neutral 의미로 down 색(빨강 X는 CSS, 여기선 표기용)
    sub: summary.asOf ? `기준일 ${formatDisplayDate(summary.asOf)}` : summary.loaded ? "미수 데이터 없음" : "조회 중…",
    spark: [],
    sparkLabels: [],
    sparkUnit: "억",
  };
}

/** 관급 잔량 KPI — 총 잔량 Σ.totalRemain(㎥). 스냅샷 */
function buildKpiAllocation(
  summary: SalesAllocationsSummary,
  _settings?: WidgetSettings,
): typeof KPI_DATA[number] {
  void _settings;
  const cur = summary.totalRemain;
  const pct = summary.totalAllocated > 0 ? (summary.totalShipped / summary.totalAllocated) * 100 : 0;
  return {
    id: "kpi-alloc",
    label: "관급 잔량",
    value: Math.round(cur).toLocaleString("ko-KR"),
    unit: "㎥",
    delta: `${pct.toFixed(0)}% 출하`,
    up: true,
    sub: summary.loaded
      ? `배정 ${Math.round(summary.totalAllocated).toLocaleString("ko-KR")} · 출하 ${Math.round(summary.totalShipped).toLocaleString("ko-KR")}`
      : "조회 중…",
    spark: [],
    sparkLabels: [],
    sparkUnit: "㎥",
  };
}

/** 금일 진척률 KPI — 오늘 예정 대비 실출하 %. 당일 단일이라 sparkline 없음 */
function buildKpiTodayProgress(
  shipments: RqcShipment[],
  _settings?: WidgetSettings,
  unit = "㎥",
  planLabel = "예정",
): typeof KPI_DATA[number] {
  void _settings;
  const s = summarize(shipments);
  const planned = s.qtyTotal;
  const shipped = s.shipTotal;
  const pct = planned > 0 ? (shipped / planned) * 100 : 0;
  return {
    id: "kpi-today",
    label: "금일 진척률",
    value: pct.toFixed(0),
    unit: "%",
    delta: `잔량 ${fmt(s.remainTotal)}`,
    up: pct >= 100,
    sub: `${planLabel} ${fmt(planned)}${unit} / 출하 ${fmt(shipped)}${unit}`,
    spark: [],
    sparkLabels: [],
    sparkUnit: "%",
  };
}

const KPI_DATA = [
  { id: "kpi-ship", label: "당월 출하량", value: "12,847", unit: "㎥", delta: "+8.2%", up: true, sub: "전월 대비",
    spark: [12, 14, 11, 16, 15, 19, 17, 22, 24, 26, 23, 28],
    sparkLabels: ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"],
    sparkUnit: "천㎥" },
  { id: "kpi-rev", label: "당월 매출", value: "2.41", unit: "억원", delta: "+12.4%", up: true, sub: "목표 대비 84%",
    spark: [1.2,1.3,1.5,1.6,1.7,1.9,2.0,2.2,2.3,2.4,2.3,2.41],
    sparkLabels: ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"],
    sparkUnit: "억" },
  { id: "kpi-price", label: "평균 단가", value: "78,200", unit: "원/㎥", delta: "-1.1%", up: false, sub: "전월 대비",
    spark: [82,81,80,80,79,79,78.5,78.4,78.3,78.2,78.2,78.2],
    sparkLabels: ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"],
    sparkUnit: "천원" },
  { id: "kpi-fleet", label: "가동 차량", value: "24", unit: "/ 28대", delta: "85.7%", up: true, sub: "평균 회전 4.2회",
    spark: [20,21,22,21,23,22,24,23,24,25,24,24],
    sparkLabels: ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"],
    sparkUnit: "대" },
  { id: "kpi-receipt", label: "당월 수금", value: "—", unit: "억원", delta: "—", up: true, sub: "조회 중…",
    spark: [], sparkLabels: [], sparkUnit: "억" },
  { id: "kpi-ar", label: "미수 잔액", value: "—", unit: "억원", delta: "—", up: false, sub: "조회 중…",
    spark: [], sparkLabels: [], sparkUnit: "억" },
  { id: "kpi-alloc", label: "관급 잔량", value: "—", unit: "㎥", delta: "—", up: true, sub: "조회 중…",
    spark: [], sparkLabels: [], sparkUnit: "㎥" },
  { id: "kpi-today", label: "금일 진척률", value: "—", unit: "%", delta: "—", up: true, sub: "조회 중…",
    spark: [], sparkLabels: [], sparkUnit: "%" },
];

// 금일 출하 내용 — /dispatch-view와 동일한 useRqcShipments(kstToday()) 소스 사용
type ShipCategory = "관급" | "사급";
type ShipStatus = "진행" | "완료" | "대기";
interface TodayShipRow {
  category: ShipCategory;
  customer: string;
  site: string;
  spec: string;
  code: string;
  planned: number;
  shipped: number;
  remaining: number;
  status: ShipStatus;
  note: string;
}

const STATUS_EPS = 0.05;
function deriveShipStatus(r: RqcShipment): ShipStatus {
  if (r.shipped > 0 && r.remain <= STATUS_EPS) return "완료";
  if (r.shipped > 0) return "진행";
  return "대기";
}

function rqcToTodayRow(r: RqcShipment): TodayShipRow {
  return {
    category: classifyTier(r) === "gov" ? "관급" : "사급",
    customer: r.company,
    site: r.site,
    spec: r.mix,
    code: r.code,
    planned: r.qty,
    shipped: r.shipped,
    remaining: r.remain,
    status: deriveShipStatus(r),
    note: r.memo1,
  };
}
const STATUS_COLORS: Record<ShipStatus, { bg: string; fg: string }> = {
  "완료":   { bg: "var(--c-accent-2)", fg: "var(--c-accent)" },
  "진행": { bg: "var(--c-info-2)",   fg: "var(--c-info)" },
  "대기":   { bg: "var(--c-warn-2)",   fg: "var(--c-warn)" },
};
const CATEGORY_COLORS: Record<ShipCategory, { bg: string; fg: string }> = {
  "관급": { bg: "var(--c-primary-50)", fg: "var(--c-primary-700)" },
  "사급": { bg: "var(--c-accent-2)",   fg: "var(--c-accent)" },
};

// 컬럼 정의 — order/widths 는 localStorage 에 별도 보관 (위젯 layout 과 분리)
type ShipColKey = "category" | "customer" | "site" | "spec" | "code" | "planned" | "shipped" | "remaining" | "status" | "note";
interface ShipColDef {
  key: ShipColKey;
  label: string;
  resizable: boolean;
  defaultWidth: number;
  align: "left" | "right" | "center";
}
const SHIP_COLS: Record<ShipColKey, ShipColDef> = {
  category:  { key: "category",  label: "구분",        resizable: true, defaultWidth: 70,  align: "center" },
  customer:  { key: "customer",  label: "거래처",      resizable: true, defaultWidth: 140, align: "left" },
  site:      { key: "site",      label: "현장명",      resizable: true, defaultWidth: 240, align: "left" },
  spec:      { key: "spec",      label: "규격",        resizable: true, defaultWidth: 100, align: "center" },
  code:      { key: "code",      label: "코드",        resizable: true, defaultWidth: 70,  align: "center" },
  planned:   { key: "planned",   label: "예정량(㎥)",  resizable: true, defaultWidth: 100, align: "right" },
  shipped:   { key: "shipped",   label: "출하량(㎥)",  resizable: true, defaultWidth: 100, align: "right" },
  remaining: { key: "remaining", label: "잔량(㎥)",    resizable: true, defaultWidth: 90,  align: "right" },
  status:    { key: "status",    label: "상태",        resizable: true, defaultWidth: 80,  align: "center" },
  note:      { key: "note",      label: "비고",        resizable: true, defaultWidth: 160, align: "left" },
};
// 기본 노출 컬럼 — code/note 는 기본 비노출(폭 절약, PC 가로스크롤 해소).
// SHIP_COLS 에는 그대로 정의되어 있어 컬럼 메뉴에서 복원 가능.
const DEFAULT_COL_ORDER: ShipColKey[] = ["category", "customer", "site", "spec", "planned", "shipped", "remaining", "status"];
const ALL_SHIP_COL_KEYS: ShipColKey[] = ["category", "customer", "site", "spec", "code", "planned", "shipped", "remaining", "status", "note"];
// v2: 기본 컬럼에서 code/note 제외 → 기존 v1 사용자도 새 기본으로 1회 리셋
const SHIP_COL_STORAGE_KEY = "rmc-today-ship-cols-v2";

// ── 사업 보드별 저장(2026-10-23) ─────────────────────────────────
// 홈 배치·위젯 설정·금일 출하 내용 열 설정은 사업 홈(중앙 레미콘 / 경원 아스콘 …)마다 따로 둔다.
// localStorage 키 = 옛 키 + ":" + 보드. 보드 키가 아직 없으면 옛 키(전 사업 공용 한 벌)를 복사본으로 읽는다.
// 개인 취향(도트 모드)은 사업과 무관하므로 공용 키 그대로.

/** 지금 홈의 보드 키 — 사업 메뉴 주소가 아니면(법인 공통·내 업무) 그 법인의 기본 사업 */
/** 지금 보는 사업 홈의 (법인, 사업) — 보드 키·부서 문서 경로의 기준 */
function useHomeBoardUnit(): RoleUnit {
  const { tenant, biz, dataTenant } = useSpace();
  const t = tenant ?? dataTenant;
  return useMemo(() => ({ tenant: t, biz: tenantHasBiz(t, biz) ? biz : defaultBiz(t) }), [t, biz]);
}
function useHomeBoard(): string {
  const u = useHomeBoardUnit();
  return homeBoardKey(u.tenant, u.biz);
}

function boardStorageKey(base: string, board: string): string {
  return `${base}:${board}`;
}

/** 보드 키 → 없으면 옛 공용 키 순서로 읽는다 */
function readBoardStorage(base: string, board: string): string | null {
  return localStorage.getItem(boardStorageKey(base, board)) ?? localStorage.getItem(base);
}

type CompactSize = "sm" | "tall" | "wide" | "full";

interface Widget {
  id: string;
  type: HomeWidgetType;
  // Desktop (12-col 절대 좌표)
  x: number;
  y: number;
  w: number;
  h: number;
  kpiIndex?: number;
  // Compact (fold/mobile 전용 — desktop 에는 영향 없음)
  compactOrder?: number;        // 좌상단부터 0,1,2,... 정렬 키 (미설정시 y*12+x 에서 도출)
  compactSize?: CompactSize;    // 4-옵션 사이즈 (미설정시 desktop w/h 에서 도출)
  // 위젯별 설정 (기간/데이터/스타일) — 미설정 시 DEFAULT_SETTINGS 적용
  settings?: WidgetSettings;
}

/** compact 정렬 키 — 명시 없으면 desktop (y, x) 에서 도출 */
function effectiveOrder(w: Widget): number {
  return w.compactOrder ?? w.y * 12 + w.x;
}
/** compact 사이즈 — 명시 없으면 desktop w 에서 도출 (KPI/작은건 sm, 큰건 full) */
function effectiveSize(w: Widget): CompactSize {
  if (w.compactSize) return w.compactSize;
  // 캘린더는 6주 격자 + 하단 요약이라 1행(180px)엔 못 들어간다 → 전체
  if (w.type === "calendar") return "full";
  return w.w <= 4 ? "sm" : "full";
}

/** 4-옵션 사이즈 라벨 + grid spans */
const SIZE_OPTIONS: { key: CompactSize; label: string; cols: number; rows: number }[] = [
  { key: "sm",   label: "작게",  cols: 3, rows: 1 },
  { key: "tall", label: "세로",  cols: 3, rows: 2 },
  { key: "wide", label: "가로",  cols: 6, rows: 1 },
  { key: "full", label: "전체",  cols: 6, rows: 2 },
];

/** 사이즈 picker 아이콘 — 비례 사각형으로 시각화 */
function SizeIcon({ size, active }: { size: CompactSize; active: boolean }) {
  const opt = SIZE_OPTIONS.find((s) => s.key === size)!;
  const baseW = (opt.cols / 6) * 14 + 2;
  const baseH = (opt.rows / 2) * 14 + 2;
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <rect
        x={(16 - baseW) / 2}
        y={(16 - baseH) / 2}
        width={baseW}
        height={baseH}
        rx="1.5"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

const DEFAULT_LAYOUT: Widget[] = [
  { id: "today2",    type: "today2", x: 0, y: 0, w: 12, h: 2 },
  { id: "kpi-ship",  type: "kpi",   x: 0, y: 2, w: 3, h: 2, kpiIndex: 0 },
  { id: "kpi-rev",   type: "kpi",   x: 3, y: 2, w: 3, h: 2, kpiIndex: 1 },
  { id: "kpi-price", type: "kpi",   x: 6, y: 2, w: 3, h: 2, kpiIndex: 2 },
  { id: "kpi-fleet", type: "kpi",   x: 9, y: 2, w: 3, h: 2, kpiIndex: 3 },
  { id: "bar",       type: "bar",       x: 0, y: 4, w: 6, h: 4 },
  { id: "line",      type: "line",      x: 6, y: 4, w: 6, h: 4 },
  { id: "bar-combo", type: "bar-combo", x: 0, y: 8, w: 12, h: 4 },
  { id: "table",     type: "table",     x: 0, y: 12, w: 12, h: 5 },
];

// 위젯 카탈로그·출처 권한·노출 판정은 domain/home_widgets.ts (2026-11-02 분리 — 그룹 편집기와 공유).
// 그룹(부서) 설정에서 끈 위젯은 useRoleHome().hidden 으로 받아 canShowWidget 에 넘긴다.
// v2: today 위젯 추가로 default layout 변경. 기존 v1 사용자는 reset 됨.
const STORAGE_KEY = "rmc-dashboard-layout-v2";

function widgetLabel(w: Widget): string {
  return widgetDef(w)?.label ?? w.id;
}
// 사용자가 "기본 배치로 저장"한 layout. 미지정 시 DEFAULT_LAYOUT(시스템 기본) 사용.
const SAVED_DEFAULT_KEY = "rmc-dashboard-saved-default-v1";

function loadSavedDefault(board: string): Widget[] | null {
  try {
    const v = readBoardStorage(SAVED_DEFAULT_KEY, board);
    if (!v) return null;
    return JSON.parse(v) as Widget[];
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Collision-aware layout — react-grid-layout 스타일
// 1) 충돌이 생기면 아래쪽 위젯을 밀어내어 해소
// 2) 빈 공간이 있으면 위로 끌어올려(gravity) compaction
// anchorId 가 지정되면 그 위젯은 자기 위치를 유지하고 나머지가 적응함
// ─────────────────────────────────────────────────────────────
function collides(a: Widget, b: Widget): boolean {
  if (a.id === b.id) return false;
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

function arrangeLayout(layout: Widget[], anchorId?: string): Widget[] {
  const result = layout.map((w) => ({ ...w }));

  // 1. 충돌 해소 — anchor 가 항상 우선 배치되도록 처리 순서 고정
  for (let iter = 0; iter < 100; iter++) {
    let changed = false;
    const order = [...result].sort((a, b) => {
      if (a.id === anchorId) return -1;
      if (b.id === anchorId) return 1;
      return a.y - b.y || a.x - b.x;
    });
    for (let i = 0; i < order.length; i++) {
      for (let j = i + 1; j < order.length; j++) {
        if (collides(order[i], order[j])) {
          order[j].y = order[i].y + order[i].h;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  // 2. 위로 끌어올림 — anchor 는 고정
  for (let iter = 0; iter < 100; iter++) {
    let changed = false;
    const sorted = [...result].sort((a, b) => a.y - b.y || a.x - b.x);
    for (const w of sorted) {
      if (w.id === anchorId) continue;
      while (w.y > 0) {
        const test = { ...w, y: w.y - 1 };
        if (result.some((o) => o.id !== w.id && collides(test, o))) break;
        w.y -= 1;
        changed = true;
      }
    }
    if (!changed) break;
  }

  return result;
}

// ─────────────────────────────────────────────────────────────
// Sparkline w/ hover
// ─────────────────────────────────────────────────────────────
function Sparkline({ pts, labels, unit, up }: { pts: number[]; labels: string[]; unit: string; up: boolean }) {
  const [hover, setHover] = useState<{ idx: number; clientX: number; clientY: number } | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  useHoverDismiss(hover != null, () => setHover(null), ref);
  const dotMode = useDotMode();
  const isDot = dotMode.enabled;
  const w = 220, h = 36;
  if (pts.length < 2) {
    return <div className="spark-wrap" style={{ position: "relative", height: h }} />;
  }
  const max = Math.max(...pts), min = Math.min(...pts);
  const range = max - min || 1;
  const xFor = (i: number) => (i / (pts.length - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * (h - 4) - 2;
  const path = pts.map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(" ");
  const fillPath = path + ` L ${w} ${h} L 0 ${h} Z`;
  const color = up ? "var(--c-accent)" : "var(--c-danger)";
  // 도트 모드 — 각 시점을 정사각형 픽셀 마커로 (스파크라인은 거의 native 크기라 왜곡 적음)
  const dotMarkers = isDot
    ? pts.map((v, i) => {
        const cx = xFor(i), cy = yFor(v);
        const s = 4.5;
        return <rect key={i} x={cx - s / 2} y={cy - s / 2} width={s} height={s} fill={color} shapeRendering="crispEdges" />;
      })
    : null;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    let idx = Math.round((px / w) * (pts.length - 1));
    idx = Math.max(0, Math.min(pts.length - 1, idx));
    setHover({ idx, clientX: e.clientX, clientY: e.clientY });
  };
  return (
    <div className="spark-wrap" style={{ position: "relative" }}>
      <svg ref={ref} className="kpi-spark" width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={`${unit} 추이`}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {isDot ? (
          dotMarkers
        ) : (
          <>
            <path d={fillPath} fill={color} opacity="0.12" />
            <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
          </>
        )}
        {hover && (
          <g>
            <line x1={xFor(hover.idx)} x2={xFor(hover.idx)} y1={0} y2={h} stroke={color} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.6" />
            <circle cx={xFor(hover.idx)} cy={yFor(pts[hover.idx])} r="2.4" fill="white" stroke={color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </svg>
      {hover && (
        <ChartTooltip x={hover.clientX} y={hover.clientY}>
          <div className="tip-label">{labels[hover.idx]}</div>
          <div className="tip-row"><span className="tip-dot" style={{ background: color }} />{pts[hover.idx].toLocaleString()} {unit}</div>
        </ChartTooltip>
      )}
    </div>
  );
}


/** Dashboard 위젯이 공통으로 참조하는 "현재 연도". KST 기준. */
function currentYearKst(): number {
  return new Date().getFullYear();
}

// ─────────────────────────────────────────────────────────────
// 원재료비 추이 — 결합제/잔골재/굵은골재 각 첫 번째 활성 자재의 월별 단가
// ─────────────────────────────────────────────────────────────
function pickRepresentative(
  cats: MaterialCategory[],
  kind: MaterialCategoryKind,
): MaterialCategory | null {
  const candidates = cats
    .filter((c) => c.category === kind && c.is_active !== false)
    .sort(
      (a, b) =>
        (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
          (b.sort_order ?? Number.MAX_SAFE_INTEGER) ||
        a.code.localeCompare(b.code),
    );
  return candidates[0] ?? null;
}

/** 해당 월 말일 기준 유효 단가 (effective_date <= last day of month인 최신 record) */
function priceAtMonthEnd(
  prices: RawMaterialPrice[],
  code: string,
  year: number,
  month: number,
): number | null {
  const lastDay = new Date(year, month, 0).toISOString().slice(0, 10);
  let best: RawMaterialPrice | null = null;
  for (const p of prices) {
    if (p.code !== code) continue;
    if (p.effective_date > lastDay) continue;
    if (!best || p.effective_date > best.effective_date) best = p;
  }
  return best ? best.price : null;
}

function LineChart({
  settings,
  onOpenSettings,
}: {
  settings: WidgetSettings;
  onOpenSettings?: () => void;
}) {
  const ALL_MONTHS = ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"];
  const { data: cats } = useMaterialCategories();
  const { data: prices } = useRawMaterials();

  const period = settings.period ?? "year";
  const selectedPaths = settings.dataPaths ?? [];

  const { startIdx, endIdx } = periodSlice(period);
  const months = ALL_MONTHS.slice(startIdx, endIdx + 1);

  const series: ChartSeries[] = useMemo(() => {
    const year = currentYearKst();
    let chosen: MaterialCategory[];
    if (selectedPaths.length > 0) {
      const codes = new Set(
        selectedPaths.map((p) => p.split(".")[2]).filter((c): c is string => !!c),
      );
      chosen = cats.filter((c) => codes.has(c.code));
    } else {
      const kinds: MaterialCategoryKind[] = ["결합제", "잔골재", "굵은골재"];
      chosen = kinds
        .map((k) => pickRepresentative(cats, k))
        .filter((c): c is MaterialCategory => c !== null);
    }
    return chosen.map((c, i) => {
      const rawAll = ALL_MONTHS.map((_, mi) => priceAtMonthEnd(prices, c.code, year, mi + 1));
      const raw = rawAll.slice(startIdx, endIdx + 1);
      const vals = raw.map((v) => (v == null ? null : Math.round(v / 1000)));
      return {
        code: c.code,
        name: c.name,
        color: paletteColor(i),
        unitForTooltip: `천원/${getMatUnit(c)}`,
        raw,
        vals,
      };
    });
  }, [cats, prices, selectedPaths, startIdx, endIdx]);

  const [focusedCode, setFocusedCode] = useState<string | null>(null);

  return (
    <>
      <ChartCardHeader
        title="원재료비 추이"
        subtitle="자재별 월별 단가 추이"
        series={series}
        focusEnabled={settings.focusEnabled}
        focusedCode={focusedCode}
        onHover={setFocusedCode}
      />
      <div className="chart-fill">
        <MultiSeriesChart
          labels={months}
          series={series}
          options={settings}
          emptyText="단가 데이터 없음"
          onOpenSettings={onOpenSettings}
          focusedCode={focusedCode}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// BarChartShipments — 월별 출하 (관급/사급/합계)
// 기본 stylePreset=bar, dataPaths 미선택 시 관급+사급 (합계 제외)
// ─────────────────────────────────────────────────────────────
const SHIPMENT_TIER_COLORS: Record<string, string> = {
  gov: CHART_SEMANTIC.gov,
  cust: CHART_SEMANTIC.cust,
  total: CHART_SEMANTIC.neutral, // 중립 강조선
};
const SHIPMENT_TIER_LABELS: Record<string, string> = {
  gov: "관급",
  cust: "사급",
  total: "합계",
};

function buildShipmentSeries(
  monthly: MonthlyBucket[],
  startIdx: number,
  endIdx: number,
  selectedTiers: string[],
  unit = "㎥",
): ChartSeries[] {
  const tiers = selectedTiers.length > 0 ? selectedTiers : ["gov", "cust"];
  return tiers
    .filter((t) => t === "gov" || t === "cust" || t === "total")
    .map((tier) => {
      const sliced = monthly.slice(startIdx, endIdx + 1);
      const raw = sliced.map((m) => {
        if (tier === "gov") return m.govM3;
        if (tier === "cust") return m.custM3;
        return m.totalM3;
      });
      return {
        code: tier,
        name: SHIPMENT_TIER_LABELS[tier] ?? tier,
        color: SHIPMENT_TIER_COLORS[tier] ?? CHART_SEMANTIC.neutral,
        unitForTooltip: unit,
        raw,
        vals: raw.map((v) => Math.round(v)),
      };
    });
}

function selectedTiersFromPaths(paths: string[]): string[] {
  return paths
    .map((p) => p.split(".")[2])
    .filter((t): t is string => !!t);
}

function BarChartShipments({
  settings,
  onOpenSettings,
}: {
  settings: WidgetSettings;
  onOpenSettings?: () => void;
}) {
  const ALL_MONTHS = ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"];
  const { monthly } = useHomeMonthly(currentYearKst());
  const { unit } = useHomeShipKind();

  const period = settings.period ?? "year";
  const { startIdx, endIdx } = periodSlice(period);
  const months = ALL_MONTHS.slice(startIdx, endIdx + 1);

  const series = useMemo(
    () => buildShipmentSeries(monthly, startIdx, endIdx, selectedTiersFromPaths(settings.dataPaths ?? []), unit),
    [monthly, startIdx, endIdx, settings.dataPaths, unit],
  );

  const effectiveSettings: WidgetSettings = { ...settings, stylePreset: settings.stylePreset ?? "bar" };

  const [focusedCode, setFocusedCode] = useState<string | null>(null);

  return (
    <>
      <ChartCardHeader
        title="월별 출하량 추이"
        subtitle="관급·사급"
        series={series}
        focusEnabled={effectiveSettings.focusEnabled}
        focusedCode={focusedCode}
        onHover={setFocusedCode}
      />
      <div className="chart-fill">
        <MultiSeriesChart
          labels={months}
          series={series}
          options={effectiveSettings}
          emptyText="출하 데이터 없음"
          onOpenSettings={onOpenSettings}
          focusedCode={focusedCode}
        />
      </div>
    </>
  );
}

function BarChartComboShipments({
  settings,
  onOpenSettings,
}: {
  settings: WidgetSettings;
  onOpenSettings?: () => void;
}) {
  const ALL_MONTHS = ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"];
  const { monthly } = useHomeMonthly(currentYearKst());
  const { unit } = useHomeShipKind();

  const period = settings.period ?? "year";
  const { startIdx, endIdx } = periodSlice(period);
  const months = ALL_MONTHS.slice(startIdx, endIdx + 1);

  const selected = selectedTiersFromPaths(settings.dataPaths ?? []);
  const series = useMemo(() => {
    const tiers = selected.length > 0 ? selected : ["gov", "cust", "total"];
    return buildShipmentSeries(monthly, startIdx, endIdx, tiers, unit);
  }, [monthly, startIdx, endIdx, selected, unit]);

  const effectiveSettings: WidgetSettings = { ...settings, stylePreset: settings.stylePreset ?? "mixed" };
  const mixedLineFn = (s: ChartSeries) => s.code === "total";

  const [focusedCode, setFocusedCode] = useState<string | null>(null);

  return (
    <>
      <ChartCardHeader
        title="월별 출하량 + 합계선"
        subtitle="관급·사급 막대 + 합계 라인"
        series={series}
        focusEnabled={effectiveSettings.focusEnabled}
        focusedCode={focusedCode}
        onHover={setFocusedCode}
      />
      <div className="chart-fill">
        <MultiSeriesChart
          labels={months}
          series={series}
          options={effectiveSettings}
          emptyText="출하 데이터 없음"
          mixedLineFn={mixedLineFn}
          onOpenSettings={onOpenSettings}
          focusedCode={focusedCode}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// 금일 출하 내용 — 컬럼 재배치 가능, 거래처/현장명/비고 폭 조절 가능
// 컬럼 설정은 위젯 layout 과 별도 localStorage 키에 저장.
// ─────────────────────────────────────────────────────────────
interface ShipColConfig {
  order: ShipColKey[];
  widths: Partial<Record<ShipColKey, number>>;
}

function useShipColConfig(): [ShipColConfig, React.Dispatch<React.SetStateAction<ShipColConfig>>] {
  // 보드가 바뀌면 홈 전체가 다시 마운트되므로(Dashboard key) 초기값만 보드 키로 읽으면 된다
  const board = useHomeBoard();
  const [config, setConfig] = useState<ShipColConfig>(() => {
    try {
      const v = readBoardStorage(SHIP_COL_STORAGE_KEY, board);
      if (v) {
        const parsed = JSON.parse(v) as ShipColConfig;
        // 새 컬럼이 추가됐을 때 누락된 키 보정
        const missing = DEFAULT_COL_ORDER.filter((k) => !parsed.order.includes(k));
        return { order: [...parsed.order.filter((k) => k in SHIP_COLS), ...missing], widths: parsed.widths || {} };
      }
    } catch { /* noop */ }
    return { order: DEFAULT_COL_ORDER, widths: {} };
  });
  useEffect(() => {
    try { localStorage.setItem(boardStorageKey(SHIP_COL_STORAGE_KEY, board), JSON.stringify(config)); } catch { /* noop */ }
  }, [config, board]);
  return [config, setConfig];
}

type SortDir = "asc" | "desc";

/** MO 홈 [상세내용 확인하기] 버튼이 카드 목록 하단을 덮는 높이(px) — CSS .ship-mo-more 의 margin-top 과 동일 */
const MO_MORE_OVERLAP = 34;
/** MO 홈 카드 미리보기 장수 — 이보다 많을 때만 버튼이 다음 카드에 걸치고 "외 N건" 을 단다 */
const MO_PREVIEW_COUNT = 2;
const MO_STATUS_RANK: Record<ShipmentStatus, number> = { in_progress: 0, waiting: 1, done: 2 };

function TodayShipmentList() {
  const [config, setConfig] = useShipColConfig();
  // 4자리 배합 코드 열은 shipment.mix 있을 때만(규격 열은 그대로). 저장된 열 설정은 건드리지 않고 표시만 뺀다.
  // 화면 가림만 — 출하 자료에 코드가 같이 온다(기사용 사본 분리는 부서별-권한 §5-D).
  const canMix = useShipmentMixPerm();
  const shownCols = canMix ? config.order : config.order.filter((k) => k !== "code");
  const menuCols = canMix ? ALL_SHIP_COL_KEYS : ALL_SHIP_COL_KEYS.filter((k) => k !== "code");
  const [draggedCol, setDraggedCol] = useState<ShipColKey | null>(null);
  const [hoverCol, setHoverCol] = useState<ShipColKey | null>(null);
  // sort 는 component state — 새로고침/페이지 이동 시 자동 초기화 (localStorage 미저장)
  const [sort, setSort] = useState<{ key: ShipColKey; dir: SortDir } | null>(null);

  const today = new Date();
  const dateLabel = today.toLocaleDateString("ko-KR", {
    year: "numeric", month: "long", day: "numeric", weekday: "long",
  });

  // 경원 아스콘 공간 = OKIS 미러(수주·출하·잔량 톤, 규격 = 아스콘 규격, 비고 = 위치·메모)
  const home = useHomeToday();
  const { shipments, loading, lastUpdatedAt, refresh, lastShipment, ascon, unit, planLabel } = home;
  const rawRows = useMemo(() => shipments.map(rqcToTodayRow), [shipments]);
  // 열 머리 단위·계획 이름 — 경원은 "수주량(톤)" (저장된 열 설정은 두 공간 공용)
  const colLabel = (k: ShipColKey) => SHIP_COLS[k].label.replace("㎥", unit).replace("예정량", `${planLabel}량`);
  // 경원 스케쥴과 같이 수주·출하·잔량 소수점 버림(10-22 사용자 지정), 중앙은 소수 첫째 자리 그대로
  const fmtQty = (v: number) => (ascon ? fmt(Math.trunc(v), 0) : v.toFixed(1));
  // 경원 MO 카드 규격 칩 — 경원 출하조회와 같은 부품(중앙이면 빈 배열이라 쓰지 않음)
  const { renderSpec } = useAsconSchedule(home.orders, home.tickets);

  // MO(≤659) 통합 카드 — 금일 출하 현황(요약) + 카드 2건 + [상세내용 확인하기] 한 장.
  // "지금 볼 것" 순서: 진행중 → 대기 → 완료, 각 그룹 안에서는 첫차 시각순.
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { metas: siteMetas } = useSiteMeta();
  const moDateLabel = today.toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "short" });
  // 카드는 전부 그리고, 타일 높이만큼만 보이게 둔다(나머지는 하단에서 흐려지며 잘림).
  // 몇 장이 보이는지는 실측 → [상세내용 확인하기] 의 "외 N건" 과 하단 페이드 on/off 에 쓴다.
  const moRows = useMemo(() => {
    const rank = (r: RqcShipment) => MO_STATUS_RANK[classifyStatus(r)];
    return [...shipments].sort((a, b) =>
      rank(a) - rank(b) ||
      (a.firstcar || "9999").localeCompare(b.firstcar || "9999"));
  }, [shipments]);
  // 2건 이하 = 카드를 다 보이고 버튼은 아래에 붙인다 / 3건 이상 = 버튼이 3번째 카드에 걸친다
  const moPeek = moRows.length > MO_PREVIEW_COUNT;
  const moCardsRef = useRef<HTMLDivElement>(null);
  const [moVisible, setMoVisible] = useState<number>(0);
  useLayoutEffect(() => {
    const box = moCardsRef.current;
    if (!box) return;
    const measure = () => {
      // 걸침 모드면 버튼이 하단 MO_MORE_OVERLAP px 를 덮으므로 그 위까지 온전히 들어간 카드만 "보이는" 것으로 센다
      const limit = box.clientHeight - (moPeek ? MO_MORE_OVERLAP : 0);
      let n = 0;
      for (const el of box.querySelectorAll<HTMLElement>(".dispatch-sched-card")) {
        if (el.offsetTop + el.offsetHeight <= limit) n++;
      }
      setMoVisible(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [moRows.length, moPeek]);
  const moHidden = Math.max(0, moRows.length - moVisible);

  // 상태 집계 — 완료/진행/대기 카운트 + 총 출하·예정량 + 진행률
  const summary = useMemo(() => {
    let done = 0, moving = 0, waiting = 0, plannedTotal = 0, shippedTotal = 0;
    for (const r of rawRows) {
      if (r.status === "완료") done += 1;
      else if (r.status === "진행") moving += 1;
      else waiting += 1;
      plannedTotal += r.planned;
      shippedTotal += r.shipped;
    }
    const pct = plannedTotal > 0 ? (shippedTotal / plannedTotal) * 100 : 0;
    return { done, moving, waiting, plannedTotal, shippedTotal, pct };
  }, [rawRows]);

  // 컬럼 표시 메뉴 (열림 상태) — code/note 등 숨긴 컬럼 복원/숨김
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!colMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) setColMenuOpen(false);
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); };
  }, [colMenuOpen]);
  const toggleCol = (key: ShipColKey) => {
    setConfig((prev) => {
      if (prev.order.includes(key)) {
        // 마지막 한 컬럼은 남겨둠
        if (prev.order.length <= 1) return prev;
        return { ...prev, order: prev.order.filter((k) => k !== key) };
      }
      // 추가 — SHIP_COLS 정의 순서(ALL_SHIP_COL_KEYS) 기준 적절한 위치에 삽입
      const targetIdx = ALL_SHIP_COL_KEYS.indexOf(key);
      const order = [...prev.order];
      let insertAt = order.length;
      for (let i = 0; i < order.length; i++) {
        if (ALL_SHIP_COL_KEYS.indexOf(order[i]) > targetIdx) { insertAt = i; break; }
      }
      order.splice(insertAt, 0, key);
      return { ...prev, order };
    });
  };

  // 헤더 클릭 시 sort 토글 (asc → desc → null 순환). 드래그가 아닌 click 에만 반응.
  const onHeaderClick = (key: ShipColKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const rows = useMemo(() => {
    if (!sort) return rawRows;
    return [...rawRows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), "ko");
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rawRows, sort]);

  // ── 컬럼 드래그 재배치 (HTML5 DnD)
  const onColDragStart = (e: React.DragEvent, key: ShipColKey) => {
    e.dataTransfer.setData("text/plain", key);
    e.dataTransfer.effectAllowed = "move";
    setDraggedCol(key);
  };
  const onColDragEnd = () => { setDraggedCol(null); setHoverCol(null); };
  const onColDragOver = (e: React.DragEvent, key: ShipColKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedCol && draggedCol !== key) setHoverCol(key);
  };
  const onColDrop = (e: React.DragEvent, targetKey: ShipColKey) => {
    e.preventDefault();
    const fromKey = e.dataTransfer.getData("text/plain") as ShipColKey;
    if (!fromKey || fromKey === targetKey) { setHoverCol(null); return; }
    setConfig((prev) => {
      const order = [...prev.order];
      const fromIdx = order.indexOf(fromKey);
      const toIdx = order.indexOf(targetKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = order.splice(fromIdx, 1);
      order.splice(fromIdx < toIdx ? toIdx - 1 : toIdx, 0, moved);
      return { ...prev, order };
    });
    setHoverCol(null);
  };

  // ── 컬럼 폭 조절 (모든 컬럼)
  const onResizeStart = (e: React.MouseEvent, key: ShipColKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = config.widths[key] ?? SHIP_COLS[key].defaultWidth;
    const onMv = (ev: MouseEvent) => {
      const newW = Math.max(60, startW + (ev.clientX - startX));
      setConfig((prev) => ({ ...prev, widths: { ...prev.widths, [key]: newW } }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMv);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMv);
    window.addEventListener("mouseup", onUp);
  };

  const colW = (key: ShipColKey) => config.widths[key] ?? SHIP_COLS[key].defaultWidth;

  // table-layout:fixed 가 정상 동작하려면 <table> 에 명시적 width 필요
  const totalW = shownCols.reduce((sum, k) => sum + colW(k), 0);

  // pill 두 컬럼은 그대로, 그 외 모두 ship-cell (block div) 로 truncate.
  const renderCell = (row: TodayShipRow, key: ShipColKey) => {
    if (key === "category") {
      const c = CATEGORY_COLORS[row.category];
      return <span className="status-pill" style={{ background: c.bg, color: c.fg }}>{row.category}</span>;
    }
    if (key === "status") {
      const c = STATUS_COLORS[row.status];
      return <span className="status-pill" style={{ background: c.bg, color: c.fg }}>{row.status}</span>;
    }
    if (key === "planned" || key === "shipped" || key === "remaining") {
      const v = fmtQty(row[key]);
      return <div className="ship-cell mono" title={v}>{v}</div>;
    }
    if (key === "code" || key === "spec") {
      const v = String(row[key]);
      return <div className="ship-cell mono" title={v}>{v}</div>;
    }
    // 거래처 / 현장명 / 비고
    const v = String(row[key]);
    return <div className="ship-cell" title={v}>{v}</div>;
  };

  return (
    <div className="ship-list">
      <div className="card-hd">
        <div>
          <div className="card-title">금일 출하 내용</div>
          <div className="card-sub">{dateLabel}</div>
        </div>
        <div className="ship-list-meta">
          <UpdatedAgo at={lastUpdatedAt} loading={loading} />
          <div className="ship-col-menu-wrap" ref={colMenuRef}>
            <button
              className="ship-refresh-btn"
              title="컬럼 설정"
              aria-label="컬럼 설정"
              onClick={() => setColMenuOpen((o) => !o)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            {colMenuOpen && (
              <div className="ship-col-menu">
                <div className="ship-col-menu-hd">표시 컬럼</div>
                {menuCols.map((k) => {
                  const on = config.order.includes(k);
                  return (
                    <label key={k} className="ship-col-menu-item">
                      <input type="checkbox" checked={on} onChange={() => toggleCol(k)} />
                      <span>{colLabel(k)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <button className="ship-refresh-btn" title="새로고침" aria-label="새로고침" onClick={refresh} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
          </button>
        </div>
      </div>
      {/* 상태 집계 바 — 완료/진행/대기 + 총건수·물량 + 진행률 */}
      <div className="ship-summary-bar">
        <div className="ship-summary-totals">
          총 {rows.length}건 · {fmt(summary.shippedTotal)} / {fmt(summary.plannedTotal)}{unit} ({summary.pct.toFixed(0)}%)
        </div>
        <div className="ship-summary-chips">
          <span className="ship-summary-chip" style={{ background: STATUS_COLORS["완료"].bg, color: STATUS_COLORS["완료"].fg }}>완료 {summary.done}</span>
          <span className="ship-summary-chip" style={{ background: STATUS_COLORS["진행"].bg, color: STATUS_COLORS["진행"].fg }}>진행 {summary.moving}</span>
          <span className="ship-summary-chip" style={{ background: STATUS_COLORS["대기"].bg, color: STATUS_COLORS["대기"].fg }}>대기 {summary.waiting}</span>
        </div>
        <div className="ship-summary-progress">
          <div className="ship-summary-progress-fill" style={{ width: `${Math.min(100, summary.pct)}%` }} />
        </div>
      </div>
      {/* MO(≤659) — 가로 스크롤 테이블 대신 출하조회와 같은 카드 2장 + 상세 진입 */}
      <div className={`ship-list-mo${moPeek ? "" : " is-fit"}`}>
        <TodayStatusCardV2View
          shipments={shipments}
          unit={unit}
          planLabel={planLabel}
          sub={home.error ?? `${moDateLabel} · ${ascon ? "아스콘 · " : ""}실시간`}
          lastShipment={lastShipment}
          layout="inline"
          actions={
            <div className="ship-mo-actions">
              <UpdatedAgo at={lastUpdatedAt} loading={loading} />
              <button className="ship-refresh-btn" title="새로고침" aria-label="새로고침" onClick={refresh} disabled={loading}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
              </button>
            </div>
          }
        />
        <div ref={moCardsRef} className={`ship-mo-cards${moHidden > 0 ? " is-overflow" : ""}`}>
          {moRows.length === 0 ? (
            <div className="ship-mo-empty">{loading ? "조회 중…" : ascon ? "금일 수주가 없습니다" : "금일 출하 예정이 없습니다"}</div>
          ) : (
            moRows.map((r) => (
              <ScheduleCard
                key={r.id}
                row={r}
                metas={siteMetas}
                variant="mini"
                onClick={() => navigate(tp("dispatch-view"))}
                title="탭하면 출하조회로 이동합니다"
                {...(ascon ? { unit, qtyLabel: planLabel, renderMix: renderSpec, fmtQty } : {})}
              />
            ))
          )}
        </div>
        <button type="button" className={`ship-mo-more${moPeek ? " is-overlay" : ""}`} onClick={() => navigate(tp("dispatch-view"))}>
          <span>상세내용 확인하기</span>
          {moHidden > 0 && <span className="ship-mo-more-cnt">외 {moHidden}건</span>}
          <Icon name="chevR" size={14} />
        </button>
      </div>
      <div className="ship-table-wrap">
        <table className="ship-table" style={{ width: totalW }}>
          <colgroup>
            {shownCols.map((k) => <col key={k} style={{ width: colW(k) }} />)}
          </colgroup>
          <thead>
            <tr>
              {shownCols.map((k) => {
                const col = SHIP_COLS[k];
                const sortActive = sort?.key === k;
                return (
                  <th
                    key={k}
                    style={{ textAlign: col.align }}
                    className={`ship-th ${draggedCol === k ? "is-dragging" : ""} ${hoverCol === k ? "is-drop-target" : ""} ${sortActive ? "is-sorted" : ""}`}
                    draggable
                    onClick={() => onHeaderClick(k)}
                    onDragStart={(e) => onColDragStart(e, k)}
                    onDragEnd={onColDragEnd}
                    onDragOver={(e) => onColDragOver(e, k)}
                    onDragLeave={() => setHoverCol((cur) => (cur === k ? null : cur))}
                    onDrop={(e) => onColDrop(e, k)}
                  >
                    <span className="ship-th-inner">
                      <span className="ship-th-label">{colLabel(k)}</span>
                      <span className={`ship-sort ${sortActive ? `is-${sort?.dir}` : ""}`} aria-hidden>
                        <svg width="8" height="12" viewBox="0 0 8 12">
                          <path className="up"   d="M4 1 L8 5 L0 5 Z" />
                          <path className="down" d="M4 11 L0 7 L8 7 Z" />
                        </svg>
                      </span>
                    </span>
                    {col.resizable && (
                      <span
                        className="ship-th-resize"
                        onMouseDown={(e) => onResizeStart(e, k)}
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.preventDefault()}
                        title="드래그하여 폭 조절"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {shownCols.map((k) => {
                  const col = SHIP_COLS[k];
                  return (
                    <td key={k} style={{ textAlign: col.align }}>
                      {renderCell(row, k)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 금일 출하 현황 — dense 디자인 (위젯 "today2").
// 헤더+상태칩 / 예정·출하·잔량·잔량% 스탯 / 카드 하단 모서리 진행률바(hover 확대).
// 출하조회 KpiGrid(dispatch-kpi-edge-progress) 패턴 차용.
function TodayStatusCardV2() {
  const { shipments, lastShipment, ascon, unit, planLabel, error, mock } = useHomeToday();
  const dateLabel = new Date().toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "short" });
  return (
    <TodayStatusCardV2View
      shipments={shipments}
      unit={unit}
      planLabel={planLabel}
      sub={error ?? `${dateLabel} · ${ascon ? `아스콘${mock ? " · 가상" : ""} · ` : ""}실시간`}
      lastShipment={lastShipment}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// 캘린더 위젯 — 월 그리드(항상 6주) + 출하일 물량 + 날짜 선택 시 하단 요약 + (best-effort) 직원 생일
//   · 날짜 클릭 = 하단 요약(출하/관급/사급/예정/회전수). 다시 클릭하면 월 합계로 복귀.
//   · [상세] 또는 더블클릭 = 출하실적의 DayDetailModal(스케쥴표+타임라인) 재사용. 출하 권한자만.
//   · 앞뒤 빈칸은 이전·다음 달 날짜로 채워 6행 고정 → 달을 넘겨도 높이가 안 흔들린다.
// ─────────────────────────────────────────────────────────────

const DayDetailModalLazy = lazy(() =>
  import("@/pages/ShipmentStats/DayDetailModal").then((m) => ({ default: m.DayDetailModal })),
);
// 경원 아스콘 공간의 그날 상세 — 경원 출하실적과 같은 모달(수주·계근, 톤)
const AsconDayModalLazy = lazy(() =>
  import("@/pages/ShipmentStats/ascon/AsconDayModal").then((m) => ({ default: m.AsconDayModal })),
);

const calPad2 = (n: number) => String(n).padStart(2, "0");
const calNum = (v: number) => Math.round(v).toLocaleString();

/** 요약 본문 — 큰 숫자 + 관급/사급 분할 바 + 메타 한 줄 */
function CalFootBody({
  main, unit, gov, cust, meta,
}: { main: number; unit: string; gov: number; cust: number; meta: ReactNode }) {
  const sum = gov + cust;
  const govPct = sum > 0 ? (gov / sum) * 100 : 0;
  return (
    <div className="cal-foot-body">
      <div className="cal-foot-main">
        {calNum(main)}<em>{unit}</em>
      </div>
      <div className="cal-foot-detail">
        <div className={`cal-split${sum > 0 ? "" : " is-empty"}`} aria-hidden>
          <span className="cal-split-gov" style={{ width: `${govPct}%` }} />
          <span className="cal-split-cust" style={{ width: `${100 - govPct}%` }} />
        </div>
        <div className="cal-foot-meta">{meta}</div>
      </div>
    </div>
  );
}
function CalMeta({ tone, label, value }: { tone?: "gov" | "cust"; label: string; value: string }) {
  return (
    <span className="cal-meta">
      {tone ? <i className={`cal-dot is-${tone}`} aria-hidden /> : null}
      <span className="cal-meta-label">{label}</span> {value}
    </span>
  );
}

function CalendarWidget({ settings }: { settings?: WidgetSettings }) {
  const showShipments = settings?.calShowShipments !== false; // 기본 ON
  const showBirthdays = settings?.calShowBirthdays === true;  // 기본 OFF
  const { user, hasPerm } = useAuth();
  // 생일 데이터는 users read 권한이 필요(rules: admin|self) → 관리자만 best-effort.
  const canBirthdays = showBirthdays && !!user?.isAdmin;
  // 그날 상세(DayDetailModal)는 RQC 라이브 조회를 타므로 출하 권한자에게만 연다.
  const canDetail = showShipments && (hasPerm("shipment.view") || hasPerm("dispatch.view"));
  const { config: workdayConfig } = useWorkdayConfig();

  const now = new Date();
  const todayYmdStr = calYmd(now.getFullYear(), now.getMonth() + 1, now.getDate());
  // 표시 월 state — { year, month(1~12) }. 기본 = 이번 달.
  const [view, setView] = useState<{ y: number; m: number }>({
    y: now.getFullYear(),
    m: now.getMonth() + 1,
  });
  /** 선택한 날짜(그 달의 일). null = 선택 없음 → 하단은 월 합계 */
  const [picked, setPicked] = useState<number | null>(null);
  /** 상세 모달을 연 날짜(YYYY-MM-DD) */
  const [detailDate, setDetailDate] = useState<string | null>(null);

  // 경원 아스콘 공간 = OKIS 미러 일별(실적 = 출하누계, 예정 자리 = 수주량, 회전 자리 = 계근 건수)
  const { ascon, unit, planLabel, tripsLabel } = useHomeShipKind();
  const { byDay, byDayDoc } = useHomeShipMonth(view.y, view.m, showShipments);

  // 직원 생일 — best-effort. users.birth_date(YYYY-MM-DD)에서 월·일 매칭.
  const { rows: users } = useUsers(canBirthdays);
  const birthdaysByDay = useMemo(() => {
    const map = new Map<number, string[]>();
    if (!canBirthdays) return map;
    const mm = calPad2(view.m);
    for (const u of users) {
      const bd = u.birthDate; // YYYY-MM-DD
      if (!bd || bd.length < 10) continue;
      if (bd.slice(5, 7) !== mm) continue;
      const day = Number(bd.slice(8, 10));
      if (!Number.isFinite(day) || day < 1 || day > 31) continue;
      const list = map.get(day) ?? [];
      list.push(u.name || u.loginId);
      map.set(day, list);
    }
    return map;
  }, [canBirthdays, users, view.m]);

  // 42칸 고정 — 앞은 이전 달 말일부터, 뒤는 다음 달 1일부터 채운다.
  // 계산은 domain/calendarGrid.ts (내 업무 › 일정·할 일 캘린더와 공용)
  const cells = useMemo<CalCell[]>(() => buildMonthCells(view.y, view.m), [view.y, view.m]);

  // 월 합계 — 캘린더에 그린 값 그대로 합산
  const monthSum = useMemo(() => {
    let total = 0, gov = 0, cust = 0, days = 0;
    for (const doc of byDayDoc.values()) {
      if (doc.total_m3 <= 0) continue;
      total += doc.total_m3;
      gov += doc.gov_m3;
      cust += doc.cust_m3;
      days++;
    }
    return { total, gov, cust, days };
  }, [byDayDoc]);

  const isCurMonth = view.y === now.getFullYear() && view.m === now.getMonth() + 1;

  const moveMonth = (delta: number) => {
    setPicked(null);
    setView((v) => {
      const idx = v.m - 1 + delta;
      return { y: v.y + Math.floor(idx / 12), m: ((idx % 12) + 12) % 12 + 1 };
    });
  };
  const goToday = () => {
    setPicked(null);
    setView({ y: now.getFullYear(), m: now.getMonth() + 1 });
  };
  const onCellClick = (c: CalCell) => {
    if (c.outside !== 0) {
      // 이웃 달 날짜 → 그 달로 넘어가면서 그 날짜를 선택
      const idx = view.m - 1 + c.outside;
      setView({ y: view.y + Math.floor(idx / 12), m: ((idx % 12) + 12) % 12 + 1 });
      setPicked(showShipments ? c.day : null);
      return;
    }
    if (!showShipments) return;
    setPicked((p) => (p === c.day ? null : c.day));
  };
  const openDetail = (date: string) => {
    if (canDetail) setDetailDate(date);
  };

  // 물량 강도(셀 배경 농도) — 그 달 최대 대비 4단계
  const maxVol = useMemo(() => {
    let m = 0;
    for (const v of byDay.values()) if (v > m) m = v;
    return m;
  }, [byDay]);
  const volLevel = (v: number) => {
    if (maxVol <= 0 || v <= 0) return 0;
    const r = v / maxVol;
    return r >= 0.75 ? 4 : r >= 0.5 ? 3 : r >= 0.25 ? 2 : 1;
  };

  // ── 하단 요약: 선택한 날 / 월 합계 ────────────────────────
  let foot: ReactNode = null;
  if (showShipments) {
    if (picked != null) {
      const date = calYmd(view.y, view.m, picked);
      const doc = byDayDoc.get(picked);
      const dow = CAL_WEEKDAYS[new Date(view.y, view.m - 1, picked).getDay()];
      const isToday = date === todayYmdStr;
      const isFuture = date > todayYmdStr;
      const offReason = nonWorkdayReason(date, workdayConfig);
      const actual = doc?.total_m3 ?? 0;
      const planned = doc?.total_planned_m3 ?? 0;
      const trips = doc?.trips ?? 0;
      const rate = planned > 0 ? Math.round((actual / planned) * 100) : null;
      let body: ReactNode;
      if (actual > 0) {
        body = (
          <CalFootBody
            main={actual} unit={unit} gov={doc?.gov_m3 ?? 0} cust={doc?.cust_m3 ?? 0}
            meta={
              <>
                <CalMeta tone="gov" label="관급" value={calNum(doc?.gov_m3 ?? 0)} />
                <CalMeta tone="cust" label="사급" value={calNum(doc?.cust_m3 ?? 0)} />
                {planned > 0 ? <CalMeta label={planLabel} value={`${calNum(planned)}${rate != null ? ` (${rate}%)` : ""}`} /> : null}
                {trips > 0 ? <CalMeta label={tripsLabel} value={`${calNum(trips)}${ascon ? "건" : "회"}`} /> : null}
              </>
            }
          />
        );
      } else if (planned > 0) {
        // 아직 출하 전(미래·당일 이른 시각) — 예정만
        body = (
          <CalFootBody
            main={planned} unit={`${unit} ${planLabel}`} gov={doc?.gov_planned_m3 ?? 0} cust={doc?.cust_planned_m3 ?? 0}
            meta={
              <>
                <CalMeta tone="gov" label="관급" value={calNum(doc?.gov_planned_m3 ?? 0)} />
                <CalMeta tone="cust" label="사급" value={calNum(doc?.cust_planned_m3 ?? 0)} />
              </>
            }
          />
        );
      } else {
        body = (
          <div className="cal-foot-empty">
            {isFuture ? (ascon ? "잡힌 수주가 없습니다" : "잡힌 출하 예정이 없습니다") : offReason ? `${offReason} · 출하 없음` : "출하 기록이 없습니다"}
          </div>
        );
      }
      foot = (
        <>
          <div className="cal-foot-head">
            <span className="cal-foot-date">
              {view.m}월 {picked}일 <span className="cal-foot-dow">{dow}</span>
            </span>
            {isToday ? <span className="cal-foot-tag is-today">오늘</span> : null}
            {offReason ? <span className="cal-foot-tag">{offReason}</span> : null}
            {canDetail ? (
              <button type="button" className="cal-foot-link" onClick={() => openDetail(date)}>
                상세 <Icon name="chevR" size={12} />
              </button>
            ) : null}
          </div>
          {body}
        </>
      );
    } else {
      const perDay = monthSum.days > 0 ? monthSum.total / monthSum.days : 0;
      foot = (
        <>
          <div className="cal-foot-head">
            <span className="cal-foot-date">{view.m}월 합계</span>
            <span className="cal-foot-hint">날짜를 누르면 그날 출하</span>
          </div>
          {monthSum.total > 0 ? (
            <CalFootBody
              main={monthSum.total} unit={unit} gov={monthSum.gov} cust={monthSum.cust}
              meta={
                <>
                  <CalMeta tone="gov" label="관급" value={calNum(monthSum.gov)} />
                  <CalMeta tone="cust" label="사급" value={calNum(monthSum.cust)} />
                  <CalMeta label="출하" value={`${monthSum.days}일`} />
                  <CalMeta label="일평균" value={calNum(perDay)} />
                </>
              }
            />
          ) : (
            <div className="cal-foot-empty">이 달의 출하 기록이 없습니다</div>
          )}
        </>
      );
    }
  }

  return (
    <div className="cal-widget">
      <div className="cal-head">
        <div className="cal-title-wrap">
          <strong className="cal-title">{view.y}년 {view.m}월</strong>
        </div>
        <div className="cal-nav-group">
          <button type="button" className="cal-nav" onClick={() => moveMonth(-1)} aria-label="이전 달">
            <span className="cal-nav-prev"><Icon name="chevR" size={14} /></span>
          </button>
          <button type="button" className="cal-nav cal-nav-today" onClick={goToday} disabled={isCurMonth && picked == null}>
            오늘
          </button>
          <button type="button" className="cal-nav" onClick={() => moveMonth(1)} aria-label="다음 달">
            <Icon name="chevR" size={14} />
          </button>
        </div>
      </div>
      <div className="cal-grid cal-dow">
        {CAL_WEEKDAYS.map((w, i) => (
          <div key={w} className={`cal-dow-cell${i === 0 ? " is-sun" : i === 6 ? " is-sat" : ""}`}>
            {w}
          </div>
        ))}
      </div>
      <div className="cal-grid cal-days">
        {cells.map((c, i) => {
          const wd = i % 7;
          const inMonth = c.outside === 0;
          const isToday = c.date === todayYmdStr;
          const isPicked = inMonth && picked === c.day;
          const vol = inMonth && showShipments ? byDay.get(c.day) : undefined;
          const hasShip = (vol ?? 0) > 0;
          const off = inMonth && !isWorkday(c.date, workdayConfig);
          const bdays = inMonth ? birthdaysByDay.get(c.day) : undefined;
          const cls = [
            "cal-cell",
            inMonth ? "" : "is-outside",
            wd === 0 ? "is-sun" : wd === 6 ? "is-sat" : "",
            off ? "is-off" : "",
            isToday ? "is-today" : "",
            isPicked ? "is-picked" : "",
            hasShip ? `has-ship lv-${volLevel(vol as number)}` : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={c.date}
              type="button"
              className={cls}
              aria-pressed={isPicked}
              aria-label={`${c.date}${hasShip ? ` 출하 ${calNum(vol as number)}${unit}` : ""}`}
              title={[
                hasShip ? `출하 ${calNum(vol as number)}${unit}` : null,
                bdays && bdays.length > 0 ? `🎂 생일: ${bdays.join(", ")}` : null,
              ].filter(Boolean).join(" · ") || undefined}
              onClick={() => onCellClick(c)}
              onDoubleClick={() => { if (inMonth) openDetail(c.date); }}
            >
              <span className="cal-day-num">{c.day}</span>
              <span className={`cal-vol${hasShip ? "" : " is-none"}`}>{hasShip ? calNum(vol as number) : ""}</span>
              {bdays && bdays.length > 0 ? <span className="cal-bday" aria-hidden>🎂</span> : null}
            </button>
          );
        })}
      </div>
      {foot ? <div className="cal-foot">{foot}</div> : null}
      {/* 위젯 타일은 z-index 스태킹 컨텍스트라 모달이 상단바 아래로 깔린다 → body 포털 */}
      {detailDate ? createPortal(
        <Suspense fallback={null}>
          {ascon ? (
            <AsconDayModalLazy date={detailDate} onClose={() => setDetailDate(null)} />
          ) : (
            <DayDetailModalLazy
              date={detailDate}
              daily={detailDate.startsWith(`${view.y}-${calPad2(view.m)}-`) ? byDayDoc.get(Number(detailDate.slice(8, 10))) : undefined}
              onClose={() => setDetailDate(null)}
            />
          )}
        </Suspense>,
        document.body,
      ) : null}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────
// 월별 출하량 표 — 차트 없이 수량만 보는 위젯
// 연도 이동(◀ ▶) + 관급/사급/합계 + 전년 동월 대비. 데이터는 useMonthlyShipments 캐시 공유(추가 read 없음)
// ─────────────────────────────────────────────────────────────
/** clamp 없는 실측 크기 훅 — 위젯이 아주 좁/낮을 때도 실제 px 을 그대로 본다 */
function useBoxSize() {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

/** 연도 피커(3열 × 4행 = 12년) — 올해가 마지막 칸에 오도록 오름차순 */
function yearGrid(curYear: number): number[] {
  const out: number[] = [];
  for (let y = curYear - 11; y <= curYear; y++) out.push(y);
  return out;
}

/** 연도 선택 팝오버 — 논모달. 트리거는 연도 숫자만, 열면 3×4 그리드 */
function YearPicker({
  year,
  curYear,
  onPick,
}: {
  year: number;
  curYear: number;
  onPick: (y: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="mst-year" ref={wrapRef}>
      <button
        type="button"
        className={`mst-year-btn${open ? " is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {year}
      </button>
      {open && (
        <div className="mst-year-pop" role="listbox" aria-label="연도 선택">
          {yearGrid(curYear).map((y) => (
            <button
              key={y}
              type="button"
              role="option"
              aria-selected={y === year}
              className={`mst-year-cell${y === year ? " is-active" : ""}${y === curYear ? " is-cur" : ""}`}
              onClick={() => {
                onPick(y);
                setOpen(false);
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthlyShipTable() {
  const curYear = currentYearKst();
  const [year, setYear] = useState(curYear);
  // 표 영역 높이를 측정해 12개월 + 합계행이 항상 "딱" 들어가도록 밀도를 조절한다.
  // 여유가 있으면 table height:100% 로 행이 늘어나 빈 공간을 채우고,
  // 좁아지면 dense → xdense 로 행 높이를 낮춘다(중간에서 잘리지 않게).
  // 재는 대상은 표 영역(.mst-scroll)이 아니라 바깥 위젯 — 크기가 타일로만 정해진다.
  // 표 영역을 재면 밀도에 따라 머리 여백이 바뀌고 스크롤바가 생겼다 사라지며 폭·높이가 다시 바뀌어
  // 밀도·전년비 열이 번갈아 바뀌는 무한 떨림이 난다. 머리 높이는 고정값(연도 버튼 28 + 기본 여백 10)으로 뺀다.
  const [boxRef, outer] = useBoxSize();
  const measured = outer.h > 0;
  const box = { w: outer.w, h: Math.max(0, outer.h - 38) };
  // 밀도별 표 자연 높이(thead + 12개월 + 합계, 실측) — 기본 419 · dense 337 · xdense 306. 여유 2px.
  // 셋 다 안 들어가면 xdense 로 두고 표 영역만 스크롤(밀도는 바깥 크기로만 정해지므로 다시 바뀌지 않음).
  const density = !measured || box.h >= 421 ? "" : box.h >= 339 ? " is-dense" : " is-xdense";
  const rowNeed = measured ? (box.h - 26) / 13 : 30; // thead 1 + 12개월 + 합계
  const fillHeight = rowNeed <= 46; // 지나치게 성글어지는 것 방지
  // 가로 폭 배분: 월 44 + 숫자 3열 × 58(= "99,999" 여유) + 전년비 60.
  // 전년비까지 담을 폭이 안 되면 아예 감춘다(반쯤 잘려 보이는 것보다 낫다).
  const showYoy = !measured || box.w >= 44 + 58 * 3 + 60;
  const narrow = measured && box.w < 320; // 헤더 "단위: ㎥" 생략
  const { monthly, loading } = useHomeMonthly(year);
  const { monthly: prev } = useHomeMonthly(year - 1);
  const { unit } = useHomeShipKind();

  const nowMonth = curMonth();
  const fmt = (v: number) => (v > 0 ? Math.round(v).toLocaleString() : "—");
  // 합계 헤더 클릭 → 월별 합계 ↔ 누적(연초부터 그 달까지) 토글. 전년비도 같은 기준으로 바뀐다.
  const [cumulative, setCumulative] = useState(false);

  const running = (arr: MonthlyBucket[]): number[] => {
    let acc = 0;
    return arr.map((m) => (acc += m.totalM3));
  };
  const cumThis = useMemo(() => running(monthly), [monthly]);
  const cumPrev = useMemo(() => running(prev), [prev]);

  const totals = useMemo(() => {
    const gov = monthly.reduce((s, m) => s + m.govM3, 0);
    const cust = monthly.reduce((s, m) => s + m.custM3, 0);
    const prevTotal = prev.reduce((s, m) => s + m.totalM3, 0);
    return { gov, cust, total: gov + cust, prevTotal };
  }, [monthly, prev]);

  const yoy = (cur: number, base: number): { text: string; up: boolean } | null => {
    if (base <= 0 || cur <= 0) return null;
    const d = ((cur - base) / base) * 100;
    // 100% 이상은 소수점을 떼야 MO 칸(60px)에 "+108.…" 로 잘리지 않는다
    return { text: `${d >= 0 ? "+" : ""}${d.toFixed(Math.abs(d) >= 100 ? 0 : 1)}%`, up: d >= 0 };
  };

  return (
    <div className={`mst-widget${density}${narrow ? " is-narrow" : ""}`} ref={boxRef}>
      <div className="mst-head">
        <div className="mst-title-wrap">
          <span className="mst-title">월별 출하량</span>
          <span className="mst-unit">단위: {unit}</span>
        </div>
        <YearPicker year={year} curYear={curYear} onPick={setYear} />
      </div>
      <div className="mst-scroll">
        <table className={`mst-table${fillHeight ? " is-fill" : ""}`}>
          <thead>
            <tr>
              <th className="mst-c-month">월</th>
              <th className="num">관급</th>
              <th className="num">사급</th>
              <th className="num">
                <button
                  type="button"
                  className={`mst-th-toggle${cumulative ? " is-on" : ""}`}
                  onClick={() => setCumulative((c) => !c)}
                  title={cumulative ? "클릭 — 월별 합계로" : "클릭 — 연초부터의 누적으로"}
                >
                  {cumulative ? "누적" : "합계"}
                </button>
              </th>
              {showYoy ? (
                <th className="num mst-c-yoy" title={cumulative ? "전년 같은 달까지의 누적 대비" : "전년 동월 대비"}>
                  전년비
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {monthly.map((m) => {
              const future = year === curYear && m.month > nowMonth;
              const cur = year === curYear && m.month === nowMonth;
              const idx = m.month - 1;
              const shown = cumulative ? cumThis[idx] : m.totalM3;
              const base = cumulative ? cumPrev[idx] ?? 0 : prev[idx]?.totalM3 ?? 0;
              const y = future ? null : yoy(shown, base);
              return (
                <tr key={m.ym} className={`${future ? "is-future" : ""} ${cur ? "is-cur" : ""}`}>
                  <td className="mst-c-month">{m.month}월</td>
                  <td className="num">{fmt(m.govM3)}</td>
                  <td className="num">{fmt(m.custM3)}</td>
                  <td className="num mst-strong">{future ? "—" : fmt(shown)}</td>
                  {showYoy ? (
                    <td className="num mst-c-yoy">
                      {y ? <span className={`mst-yoy ${y.up ? "is-up" : "is-down"}`}>{y.text}</span> : <span className="mst-dim">—</span>}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="mst-c-month">합계</td>
              <td className="num">{fmt(totals.gov)}</td>
              <td className="num">{fmt(totals.cust)}</td>
              <td className="num mst-strong">{fmt(totals.total)}</td>
              {showYoy ? (
                <td
                  className="num mst-c-yoy"
                  title={
                    year === curYear
                      ? `전년 1~${nowMonth}월 누적 대비 (같은 기간 비교)`
                      : "전년 연간 합계 대비"
                  }
                >
                  {(() => {
                    // 진행 중인 연도는 전년 12개월 전체가 아니라 같은 달까지의 누적과 비교
                    const base = year === curYear ? cumPrev[nowMonth - 1] ?? 0 : totals.prevTotal;
                    const y = yoy(totals.total, base);
                    return y ? <span className={`mst-yoy ${y.up ? "is-up" : "is-down"}`}>{y.text}</span> : <span className="mst-dim">—</span>;
                  })()}
                </td>
              ) : null}
            </tr>
          </tfoot>
        </table>
        {loading && totals.total === 0 ? <div className="mst-loading">불러오는 중…</div> : null}
      </div>
    </div>
  );
}

function WidgetBody({
  widget,
  onOpenSettings,
}: {
  widget: Widget;
  onOpenSettings?: () => void;
}) {
  // 당월 출하량 KPI는 useMonthlyShipments로 동적 생성 — 다른 위젯도 hook 호출하므로 in-memory 캐시로 0 reads
  const curYear = currentYearKst();
  const { hasPerm } = useAuth();
  const canSales = hasPerm("sales.view");
  const canShipment = hasPerm("shipment.view") || hasPerm("dispatch.view");
  const kpiIdx = widget.type === "kpi" ? widget.kpiIndex : undefined;
  // 이 위젯이 쓰는 출처만 읽는다(year 0 = 비활성). 권한 없는 위젯은 애초에 마운트되지 않는다.
  const needShip = kpiIdx === 0;
  const needSales = canSales && (kpiIdx === 1 || kpiIdx === 2);
  const kind = useHomeShipKind();
  const { monthly } = useHomeMonthly(needShip ? curYear : 0);
  const { monthly: monthlySales } = useMonthlySales(needSales ? curYear : 0);
  const { rows: vehicles } = useVehicles();
  // 전년 동월 비교(kpiCompareBasis==="prev_year") 대비 — 연도별 in-memory 캐시라 부담 적음.
  // KPI 위젯에서 prev_year 선택 시에만 의미. 항상 호출(hook 규칙) — 미선택이면 builder가 무시.
  const { monthly: prevShip } = useHomeMonthly(needShip ? curYear - 1 : 0);
  const { monthly: prevSales } = useMonthlySales(needSales ? curYear - 1 : 0);
  // 영업 KPI(수금/미수/관급잔량) — 권한 없으면 fetch 차단(permission-denied 회피). hook은 항상 호출.
  const needReceipts = canSales && kpiIdx === 4;
  const { monthly: monthlyReceipts } = useMonthlyReceipts(needReceipts ? curYear : 0);
  const { monthly: prevReceipts } = useMonthlyReceipts(needReceipts ? curYear - 1 : 0);
  const arSummary = useSalesBalancesSummary(canSales && kpiIdx === 5);
  const allocSummary = useSalesAllocationsSummary(canSales && kpiIdx === 6);
  // 금일 진척률 — 금일출하와 동일 소스(캐시). 권한 게이팅. 경원 아스콘 공간은 OKIS 미러
  const { shipments: todayShipments } = useHomeToday(widget.type === "kpi" && kpiIdx === 7 && canShipment);
  if (widget.type === "today2") {
    return <TodayStatusCardV2 />;
  }
  if (widget.type === "calendar") {
    return <CalendarWidget settings={widget.settings} />;
  }
  if (widget.type === "kpi" && widget.kpiIndex !== undefined) {
    const s = widget.settings;
    const blocked =
      (widget.kpiIndex >= 4 && widget.kpiIndex <= 6 && !canSales) ||
      (widget.kpiIndex === 7 && !canShipment);
    const k =
      blocked ? { ...KPI_DATA[widget.kpiIndex], value: "—", delta: "—", sub: "권한 없음", spark: [] } :
      widget.kpiIndex === 0 ? buildKpiShipment(monthly, s, prevShip, kind.unit) :
      widget.kpiIndex === 1 ? buildKpiRevenue(monthlySales, s, prevSales) :
      widget.kpiIndex === 2 ? buildKpiPrice(monthlySales, s, prevSales) :
      widget.kpiIndex === 3 ? buildKpiFleet(vehicles, s) :
      widget.kpiIndex === 4 ? buildKpiReceipts(monthlyReceipts, s, prevReceipts) :
      widget.kpiIndex === 5 ? buildKpiAr(arSummary, s) :
      widget.kpiIndex === 6 ? buildKpiAllocation(allocSummary, s) :
      widget.kpiIndex === 7 ? buildKpiTodayProgress(todayShipments, s, kind.unit, kind.planLabel) :
      KPI_DATA[widget.kpiIndex];
    // 비교기준(전월/전년동월)은 인라인 텍스트로 두면 카드 폭 변동이 커서, delta 칩의 hover 툴팁으로만 노출 (기간 비교형 KPI만).
    const showCompare =
      !blocked && (widget.kpiIndex === 0 || widget.kpiIndex === 1 || widget.kpiIndex === 2 || widget.kpiIndex === 4);
    return (
      <div className="kpi-body">
        <div className="kpi-head">
          <span className="kpi-label">{k.label}</span>
          <span className="kpi-delta-wrap">
            {showCompare ? (
              <HoverTip tip={compareSubLabel(s)} className={`kpi-delta ${k.up ? "up" : "down"}`}>
                {k.up ? "▲" : "▼"} {k.delta}
              </HoverTip>
            ) : (
              <span className={`kpi-delta ${k.up ? "up" : "down"}`}>{k.up ? "▲" : "▼"} {k.delta}</span>
            )}
          </span>
        </div>
        <div className="kpi-val">
          <span className="kpi-num">{k.value}</span>
          <span className="kpi-unit">{k.unit}</span>
        </div>
        {k.sub ? <div className="kpi-sub">{k.sub}</div> : null}
        {widget.kpiIndex === 3 && !blocked
          ? <FleetCompositionBar vehicles={vehicles} />
          : k.spark.length > 0
          ? <Sparkline pts={k.spark} labels={k.sparkLabels} unit={k.sparkUnit} up={k.up} />
          : null}
      </div>
    );
  }
  if (widget.type === "bar") {
    return (
      <div className="widget-chart">
        <BarChartShipments settings={widget.settings ?? {}} onOpenSettings={onOpenSettings} />
      </div>
    );
  }
  if (widget.type === "bar-combo") {
    return (
      <div className="widget-chart">
        <BarChartComboShipments settings={widget.settings ?? {}} onOpenSettings={onOpenSettings} />
      </div>
    );
  }
  if (widget.type === "line") {
    return (
      <div className="widget-chart">
        <LineChart settings={widget.settings ?? {}} onOpenSettings={onOpenSettings} />
      </div>
    );
  }
  if (widget.type === "month-table") {
    return <MonthlyShipTable />;
  }
  if (widget.type === "table") {
    return <TodayShipmentList />;
  }
  return null;
}

interface AddWidgetMenuProps {
  open: boolean;
  onClose: () => void;
  layout: Widget[];
  /** 그룹 설정에서 끈 위젯 키 */
  hidden: ReadonlySet<string>;
  onAdd: (def: typeof AVAILABLE_WIDGETS[number]) => void;
}

function AddWidgetMenu({ open, onClose, layout, hidden, onAdd }: AddWidgetMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { hasPerm } = useAuth();
  const { ascon } = useHomeShipKind();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose]);
  if (!open) return null;
  const usedIds = new Set(layout.map((w) => w.id));
  return (
    <div className="add-widget-pop" ref={ref}>
      <div className="awp-head">위젯 추가</div>
      <div className="awp-list">
        {AVAILABLE_WIDGETS.filter((w) => canShowWidget(w, hasPerm, ascon, hidden)).map((w, i) => {
          const id = w.type === "kpi" && w.kpiIndex !== undefined
            ? kpiWidgetId(w.kpiIndex)
            : w.type;
          // multi 위젯은 항상 추가 가능 (이미 배치돼 있어도)
          const used = !w.multi && usedIds.has(id);
          return (
            <button key={i} className="awp-item" disabled={used} onClick={() => { if (!used) onAdd(w); }}>
              <span className="awp-icon"><Icon name={w.icon} size={16} /></span>
              <span className="awp-label">{w.label}</span>
              {used ? <span className="awp-badge">추가됨</span> : <Icon name="plus" size={14} style={{ opacity: 0.5 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 도트 모드 토글 + 범위 선택 팝오버 */
function DotModeMenu({
  open,
  onClose,
  value,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  value: DotModeState;
  onChange: (v: DotModeState) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose]);
  if (!open) return null;
  const scopes: { key: DotScope; label: string; desc: string }[] = [
    { key: "charts", label: "차트만", desc: "그래프만 픽셀화" },
    { key: "full", label: "전체", desc: "픽셀 폰트·테두리까지" },
  ];
  return (
    <div className="dot-mode-pop" ref={ref}>
      <button
        type="button"
        className={`dmp-toggle ${value.enabled ? "is-on" : ""}`}
        onClick={() => onChange({ ...value, enabled: !value.enabled })}
      >
        <span className="dmp-toggle-dot" aria-hidden />
        <span className="dmp-toggle-label">도트 모드 {value.enabled ? "켜짐" : "꺼짐"}</span>
        <span className={`dmp-switch ${value.enabled ? "is-on" : ""}`}><span /></span>
      </button>
      <div className="dmp-head">적용 범위</div>
      <div className="dmp-scopes" role="radiogroup" aria-label="도트 모드 범위">
        {scopes.map((s) => (
          <button
            key={s.key}
            role="radio"
            aria-checked={value.scope === s.key}
            className={`dmp-scope ${value.scope === s.key ? "is-active" : ""}`}
            disabled={!value.enabled}
            onClick={() => onChange({ ...value, scope: s.key })}
          >
            <span className="dmp-scope-label">{s.label}</span>
            <span className="dmp-scope-desc">{s.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Fold/Mobile 감지 — CSS auto-flow 가 강제되는 폭에서는 픽셀 드래그가 의미 없음. */
function useIsCompact() {
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const handler = () => setCompact(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return compact;
}

interface WidgetTileProps {
  widget: Widget;
  editMode: boolean;
  compact: boolean;
  colW: number;
  rowH: number;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, w: number, h: number) => void;
  onRemove: (id: string) => void;
  onSettings: (id: string) => void;
  onCompactReorder: (draggedId: string, targetId: string) => void;
  onCompactSetSize: (id: string, size: CompactSize) => void;
  dragging: string | null;
  setDragging: (id: string | null) => void;
}

function WidgetTile({
  widget,
  editMode,
  compact,
  colW,
  rowH,
  onMove,
  onResize,
  onRemove,
  onSettings,
  onCompactReorder,
  onCompactSetSize,
  dragging,
  setDragging,
}: WidgetTileProps) {
  const tileRef = useRef<HTMLDivElement>(null);
  const [localPos, setLocalPos] = useState<{ x: number; y: number } | null>(null);
  const [localSize, setLocalSize] = useState<{ w: number; h: number } | null>(null);

  const startDrag = (e: React.MouseEvent) => {
    if (!editMode || compact) return;
    const target = e.target as HTMLElement;
    if (target.closest(".tile-resize") || target.closest(".tile-remove")) return;
    e.preventDefault();
    const startCX = e.clientX, startCY = e.clientY;
    const sx = widget.x, sy = widget.y;
    setDragging(widget.id);
    const onMv = (ev: MouseEvent) => {
      const dx = (ev.clientX - startCX) / colW;
      const dy = (ev.clientY - startCY) / rowH;
      setLocalPos({ x: sx + dx, y: sy + dy });
    };
    const onUp = (ev: MouseEvent) => {
      const dx = (ev.clientX - startCX) / colW;
      const dy = (ev.clientY - startCY) / rowH;
      const nx = Math.round(Math.max(0, Math.min(12 - widget.w, sx + dx)));
      const ny = Math.round(Math.max(0, sy + dy));
      onMove(widget.id, nx, ny);
      setLocalPos(null);
      setDragging(null);
      window.removeEventListener("mousemove", onMv);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMv);
    window.addEventListener("mouseup", onUp);
  };

  const startResize = (e: React.MouseEvent) => {
    if (!editMode || compact) return;
    e.preventDefault();
    e.stopPropagation();
    const startCX = e.clientX, startCY = e.clientY;
    const sw = widget.w, sh = widget.h;
    setDragging(widget.id);
    const onMv = (ev: MouseEvent) => {
      const dw = (ev.clientX - startCX) / colW;
      const dh = (ev.clientY - startCY) / rowH;
      const nw = Math.max(2, Math.min(12 - widget.x, sw + dw));
      const nh = Math.max(2, sh + dh);
      setLocalSize({ w: nw, h: nh });
    };
    const onUp = (ev: MouseEvent) => {
      const dw = (ev.clientX - startCX) / colW;
      const dh = (ev.clientY - startCY) / rowH;
      const nw = Math.round(Math.max(2, Math.min(12 - widget.x, sw + dw)));
      const nh = Math.round(Math.max(2, sh + dh));
      onResize(widget.id, nw, nh);
      setLocalSize(null);
      setDragging(null);
      window.removeEventListener("mousemove", onMv);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMv);
    window.addEventListener("mouseup", onUp);
  };

  const x = localPos ? localPos.x : widget.x;
  const y = localPos ? localPos.y : widget.y;
  const w = localSize ? localSize.w : widget.w;
  const h = localSize ? localSize.h : widget.h;
  const isActive = dragging === widget.id;
  // compact 일 때만 4-옵션 사이즈 클래스 적용 (desktop 은 inline 좌표가 우선)
  const compactSize = effectiveSize(widget);
  const sizeClass = compact ? `size-${compactSize}` : "";

  // ── HTML5 drag-and-drop (compact 전용)
  const onDragStartCompact = (e: React.DragEvent) => {
    if (!editMode || !compact) return;
    e.dataTransfer.setData("text/plain", widget.id);
    e.dataTransfer.effectAllowed = "move";
    setDragging(widget.id);
  };
  const onDragEndCompact = () => setDragging(null);
  const onDragOverCompact = (e: React.DragEvent) => {
    if (!editMode || !compact) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDropCompact = (e: React.DragEvent) => {
    if (!editMode || !compact) return;
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    if (draggedId) onCompactReorder(draggedId, widget.id);
    setDragging(null);
  };

  return (
    <div
      ref={tileRef}
      className={`widget-tile ${editMode ? "is-edit" : ""} ${isActive ? "is-active" : ""} ${widget.type === "kpi" ? "is-kpi" : "is-card"} ${sizeClass}`}
      style={{
        gridColumn: `${Math.round(x) + 1} / span ${Math.round(w)}`,
        gridRow: `${Math.round(y) + 1} / span ${Math.round(h)}`,
        transform: localPos || localSize
          ? `translate(${(x - Math.round(x)) * colW}px, ${(y - Math.round(y)) * rowH}px) scale(${localSize ? 1 : 1.01})`
          : "none",
        zIndex: isActive ? 50 : 1,
      }}
      onDragOver={editMode && compact ? onDragOverCompact : undefined}
      onDrop={editMode && compact ? onDropCompact : undefined}
    >
      {editMode && !compact && (
        <div className="tile-handle" onMouseDown={startDrag} title="드래그하여 이동">
          <Icon name="menu" size={14} />
          <span>이동</span>
        </div>
      )}
      {editMode && compact && (
        <div
          className="tile-handle compact-handle"
          draggable
          onDragStart={onDragStartCompact}
          onDragEnd={onDragEndCompact}
          title="드래그하여 순서 변경"
        >
          <Icon name="menu" size={14} />
          <span>순서</span>
        </div>
      )}
      <div className="tile-inner" onMouseDown={editMode && !compact ? startDrag : undefined}>
        <WidgetBody widget={widget} onOpenSettings={() => onSettings(widget.id)} />
      </div>
      {editMode && (
        <div className="tile-edit-actions">
          <div className="tile-edit-title">{widgetLabel(widget)}</div>
          <div className="tile-edit-btns">
            <button
              type="button"
              className="tile-edit-btn"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onSettings(widget.id)}
              title="설정"
              aria-label="설정"
            >
              <Icon name="settings" size={16} />
            </button>
            <button
              type="button"
              className="tile-edit-btn is-danger"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onRemove(widget.id)}
              title="제거"
              aria-label="제거"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>
      )}
      {editMode && !compact && (
        <div className="tile-resize" onMouseDown={startResize} title="크기 조절">
          <svg width="14" height="14" viewBox="0 0 14 14"><path d="M2 12 L12 2 M6 12 L12 6 M10 12 L12 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
        </div>
      )}
      {editMode && compact && (
        <div className="tile-size-picker" role="radiogroup" aria-label="사이즈 선택">
          {SIZE_OPTIONS.map((opt) => {
            const active = opt.key === compactSize;
            return (
              <button
                key={opt.key}
                role="radio"
                aria-checked={active}
                className={`size-pick-btn ${active ? "is-active" : ""}`}
                onClick={() => onCompactSetSize(widget.id, opt.key)}
                title={`${opt.label} (${opt.cols}×${opt.rows})`}
              >
                <SizeIcon size={opt.key} active={active} />
              </button>
            );
          })}
        </div>
      )}
      {(localPos || localSize) && (
        <div className="tile-badge">
          {localSize ? `${Math.round(w)} × ${Math.round(h)}` : `${Math.round(x)+1}, ${Math.round(y)+1}`}
        </div>
      )}
    </div>
  );
}

/**
 * 홈 — 사업 보드마다 배치·설정을 따로 갖는다. 같은 라우트 요소라 중앙↔경원 이동 시 인스턴스가 이어질 수 있어
 * 보드 키로 key 를 걸어 새로 마운트한다(배치 state·원격 구독·저장 타이머가 섞이지 않게).
 */
export function Dashboard() {
  const unit = useHomeBoardUnit();
  const board = homeBoardKey(unit.tenant, unit.biz);
  return <DashboardBoard key={board} board={board} unit={unit} />;
}

function DashboardBoard({ board, unit }: { board: string; unit: RoleUnit }) {
  const [layout, setLayout] = useState<Widget[]>(() => {
    try {
      const v = readBoardStorage(STORAGE_KEY, board);
      if (v) return arrangeLayout(JSON.parse(v) as Widget[]);
    } catch { /* noop */ }
    return DEFAULT_LAYOUT;
  });
  const compact = useIsCompact();
  const confirm = useConfirm();
  const { hasPerm, user } = useAuth();
  // 경원 아스콘 공간 — 경원 자료가 있는 위젯만(출하 위젯은 OKIS 미러). 중앙 매출은 읽지 않는다
  const { ascon } = useHomeShipKind();
  // 내 부서의 홈 설정 — 끈 위젯(hidden)·부서 기본 배치(roleLayout). 관리자는 hidden 빈 집합
  const { hidden: roleHidden, roleLayout, loaded: roleLoaded } = useRoleHome(unit);
  const [roleLayoutOpen, setRoleLayoutOpen] = useState(false);
  // 평균단가 위젯 설정의 규격 선택지 — 올해 매출의 규격 합집합. useMonthlySales는 연도별 캐시라 추가 read 0.
  const { monthly: salesForSpecs } = useMonthlySales(hasPerm("sales.view") && !ascon ? currentYearKst() : 0);
  const priceSpecs = useMemo(() => collectPriceSpecs(salesForSpecs), [salesForSpecs]);
  const [editMode, setEditMode] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // 도트(픽셀아트) 모드 — 로컬 영속 (레이아웃과 별개)
  const [dotMode, setDotMode] = useState<DotModeState>(() => {
    try {
      const v = localStorage.getItem(DOT_MODE_STORAGE_KEY);
      if (v) return { ...DEFAULT_DOT_MODE, ...(JSON.parse(v) as Partial<DotModeState>) };
    } catch { /* noop */ }
    return DEFAULT_DOT_MODE;
  });
  const [dotMenuOpen, setDotMenuOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(DOT_MODE_STORAGE_KEY, JSON.stringify(dotMode)); } catch { /* noop */ }
  }, [dotMode]);
  const [dragging, setDragging] = useState<string | null>(null);
  /** 설정 모달 — 편집 중인 widget id */
  const [settingsId, setSettingsId] = useState<string | null>(null);
  /** 카테고리 — 데이터 taxonomy 빌드용 (line widget만 사용) */
  const { data: matCats } = useMaterialCategories();
  const gridRef = useRef<HTMLDivElement>(null);
  const [colW, setColW] = useState(100);
  const ROW_H = 80;

  // ── 사용자·보드별 레이아웃 (Firestore) ────────────────────────────
  // SoT 우선순위: 보드 문서 있으면 그것. 없으면 옛 공용 문서(복사본) → 그것도 없으면 localStorage 시드를 1회 업로드.
  // 초기/로딩 중에는 localStorage 즉시 표시 → 깜빡임 없음.
  const { remoteLayout, remoteApplied, legacyLayout, loaded: remoteLoaded, save: saveRemote } = useDashboardPrefs(board);
  /** 원격 적용/시드를 이미 했는지 (1회만) */
  const remoteAppliedRef = useRef(false);
  /** 레이아웃 변경이 원격 adoption 에서 비롯됐는지 — 그 경우 재저장 skip */
  const skipNextSaveRef = useRef(false);
  /** Firestore 저장 디바운스 타이머 */
  const saveTimerRef = useRef<number | null>(null);
  /** 디바운스 대기 중인 레이아웃 — 저장 전에 화면을 떠나면(다른 사업 홈으로 이동 등) 바로 저장 */
  const pendingSaveRef = useRef<Widget[] | null>(null);

  // Firestore 도착 시 1회 마이그레이션 (부서 기본 배치 문서까지 받은 뒤)
  useEffect(() => {
    if (!remoteLoaded || !roleLoaded || remoteAppliedRef.current) return;
    remoteAppliedRef.current = true;
    const roleWidgets = roleLayout && roleLayout.layout.length > 0 ? (roleLayout.layout as unknown as Widget[]) : null;
    const applyVersion = roleLayout?.applyVersion ?? 0;
    if (roleWidgets && applyVersion > remoteApplied) {
      // 관리자가 "구성원 홈에 지금 적용"으로 저장한 부서 배치가 아직 안 받아들여짐 → 교체하고 버전 기록
      skipNextSaveRef.current = true;
      setLayout(arrangeLayout(roleWidgets));
      void saveRemote(roleWidgets as DashboardWidget[], applyVersion);
    } else if (remoteLayout && remoteLayout.length > 0) {
      // 원격 문서를 SoT 로 채택 (재저장 skip — 같은 데이터 다시 안 올림)
      skipNextSaveRef.current = true;
      setLayout(arrangeLayout(remoteLayout as Widget[]));
    } else if (legacyLayout && legacyLayout.length > 0) {
      // 이 보드 문서가 처음 — 옛 공용 배치를 복사해 시작하고 바로 이 보드에 저장
      skipNextSaveRef.current = true;
      setLayout(arrangeLayout(legacyLayout as Widget[]));
      void saveRemote(legacyLayout, applyVersion);
    } else if (roleWidgets) {
      // 첫 진입(개인 배치 없음) → 부서 기본 배치로 시작
      skipNextSaveRef.current = true;
      setLayout(arrangeLayout(roleWidgets));
      void saveRemote(roleWidgets as DashboardWidget[], applyVersion);
    } else {
      // 원격 없음 → 현재 localStorage 레이아웃을 시드로 1회 업로드
      void saveRemote(layout as DashboardWidget[], applyVersion);
    }
    // layout 은 의도적으로 의존성 제외 (마운트 시점 값으로 시드). remote/role 로딩 변화만 반응.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteLoaded, roleLoaded, remoteLayout, remoteApplied, legacyLayout, roleLayout]);

  // layout 변경 → localStorage(폴백) 즉시 + Firestore(디바운스) 저장
  useEffect(() => {
    try {
      localStorage.setItem(boardStorageKey(STORAGE_KEY, board), JSON.stringify(layout));
    } catch { /* noop */ }
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    // 원격 마이그레이션 완료 후에만 Firestore 저장 (시드 전 덮어쓰기 방지)
    if (!remoteAppliedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = layout;
    saveTimerRef.current = window.setTimeout(() => {
      pendingSaveRef.current = null;
      void saveRemote(layout as DashboardWidget[]);
    }, 600);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [layout, board, saveRemote]);

  // 화면을 떠날 때 디바운스 대기분이 있으면 바로 저장 (보드 전환 = 다시 마운트)
  useEffect(() => () => {
    const pending = pendingSaveRef.current;
    if (pending) void saveRemote(pending as DashboardWidget[]);
  }, [saveRemote]);

  // 편집 완료(편집모드 off) 시 디바운스 대기 없이 즉시 저장 — 직후 새로고침에도 유실 방지
  const prevEditRef = useRef(editMode);
  useEffect(() => {
    if (prevEditRef.current && !editMode && remoteAppliedRef.current) {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      pendingSaveRef.current = null;
      void saveRemote(layout as DashboardWidget[]);
    }
    prevEditRef.current = editMode;
  }, [editMode, layout, saveRemote]);

  useEffect(() => {
    const measure = () => {
      if (!gridRef.current) return;
      const cs = getComputedStyle(gridRef.current);
      const gap = parseFloat(cs.gap) || 14;
      const totalW = gridRef.current.clientWidth;
      setColW((totalW - 11 * gap) / 12);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (gridRef.current) ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  const onMove = (id: string, x: number, y: number) =>
    setLayout((l) => arrangeLayout(l.map((w) => (w.id === id ? { ...w, x, y } : w)), id));
  const onResize = (id: string, w: number, h: number) =>
    setLayout((l) => arrangeLayout(l.map((it) => (it.id === id ? { ...it, w, h } : it)), id));
  const onRemove = (id: string) => setLayout((l) => arrangeLayout(l.filter((w) => w.id !== id)));
  const onAdd = (def: typeof AVAILABLE_WIDGETS[number]) => {
    // multi 위젯은 인스턴스마다 고유 id(타입-타임스탬프). 그 외는 type(또는 kpi suffix) 단일 id.
    const id = def.multi
      ? `${def.type}-${Date.now()}`
      : def.type === "kpi" && def.kpiIndex !== undefined
      ? kpiWidgetId(def.kpiIndex)
      : def.type;
    // multi 위젯은 중복 차단 skip (항상 추가 가능)
    if (!def.multi && layout.find((w) => w.id === id)) return;
    const maxY = layout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
    const maxOrder = layout.reduce((m, w) => Math.max(m, effectiveOrder(w)), -1);
    const next: Widget = {
      id,
      type: def.type,
      kpiIndex: def.kpiIndex,
      x: 0,
      y: maxY,
      w: def.defaultSize.w,
      h: def.defaultSize.h,
      compactOrder: maxOrder + 1,
      compactSize: def.type === "calendar" || def.defaultSize.w > 4 ? "full" : "sm",
    };
    setLayout(arrangeLayout([...layout, next], id));
    setAddOpen(false);
  };
  // 기본 배치 우선순위: 내가 저장한 기본 > 부서 기본(관리자 설정) > 시스템 초기
  const onReset = async () => {
    const saved = loadSavedDefault(board);
    const roleDefault = roleLayout && roleLayout.layout.length > 0 ? (roleLayout.layout as unknown as Widget[]) : null;
    const ok = await confirm({
      title: "기본 배치로 복원할까요?",
      summary: saved
        ? "사용자가 저장한 기본 배치로 복원합니다."
        : roleDefault
          ? "관리자가 정한 부서 기본 배치로 복원합니다."
          : "저장한 기본 배치가 없어 시스템 초기 배치로 복원합니다.",
      effects: [{ text: "현재 위젯 배치가 바뀝니다.", strong: true }],
      confirmLabel: "복원",
      kind: "warning",
    });
    if (!ok) return;
    setLayout(arrangeLayout(saved ?? roleDefault ?? DEFAULT_LAYOUT));
  };

  const onSaveAsDefault = async () => {
    const saved = loadSavedDefault(board);
    const ok = await confirm({
      title: "현재 배치를 기본으로 저장할까요?",
      summary: "이후 [기본 배치로 복원]하면 이 배치를 사용합니다.",
      effects: saved ? [{ text: "기존에 저장한 기본 배치를 덮어씁니다.", strong: true }] : undefined,
      confirmLabel: "저장",
      kind: "warning",
    });
    if (!ok) return;
    try {
      localStorage.setItem(boardStorageKey(SAVED_DEFAULT_KEY, board), JSON.stringify(layout));
    } catch { /* noop */ }
  };


  // 화면에 그릴 위젯 — 데이터 출처 권한이 없는 위젯은 빼고 위로 당겨 배치한다.
  // 저장된 배치(layout)는 그대로 둔다(권한이 다시 생기면 원래 자리로 돌아오게).
  const shownLayout = useMemo(
    () => arrangeLayout(layout.filter((w) => canShowWidget(w, hasPerm, ascon, roleHidden))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, hasPerm, ascon],
  );
  const totalRows = useMemo(() => shownLayout.reduce((m, w) => Math.max(m, w.y + w.h), 6), [shownLayout]);

  // 정렬 — compact 일 때만 compactOrder 우선, 그 외엔 desktop (y, x).
  // Desktop 은 inline gridColumn/Row 로 절대 배치되므로 정렬 영향 없음.
  // Fold/Mobile 은 auto-flow 라 DOM 순서가 곧 시각 순서가 됨.
  // MO(≤659)에서는 "금일 출하 내용" 위젯이 현황 요약까지 한 장에 담으므로
  // 같은 화면에 "금일 출하 현황"(today2) 위젯이 함께 있으면 중복 → MO 에서만 감춘다.
  // (현황 위젯만 담아둔 사용자는 그대로 보이게 — 출하 내용 위젯이 있을 때만 적용)
  // 편집 중에는 감추지 않는다 — 안 보이면 순서 변경·제거를 할 수 없다.
  const isMo = useMediaQuery("(max-width: 659px)");
  const renderOrder = useMemo(() => {
    const hasMoShipList = isMo && !editMode && shownLayout.some((w) => w.type === "table");
    const base = hasMoShipList
      ? shownLayout.filter((w) => w.type !== "today2")
      : shownLayout;
    if (compact) {
      return [...base].sort((a, b) => effectiveOrder(a) - effectiveOrder(b));
    }
    return [...base].sort((a, b) => a.y - b.y || a.x - b.x);
  }, [shownLayout, compact, isMo, editMode]);

  // Compact 전용 — 순서 재배치 (HTML5 drop 핸들러)
  const compactReorder = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    setLayout((prev) => {
      const sorted = [...prev].sort((a, b) => effectiveOrder(a) - effectiveOrder(b));
      const di = sorted.findIndex((w) => w.id === draggedId);
      const ti = sorted.findIndex((w) => w.id === targetId);
      if (di < 0 || ti < 0) return prev;
      const [moved] = sorted.splice(di, 1);
      const insertAt = di < ti ? ti - 1 : ti;
      sorted.splice(insertAt, 0, moved);
      // 정렬 결과를 0..N 으로 정규화해 모든 위젯에 compactOrder 재할당
      return prev.map((w) => ({
        ...w,
        compactOrder: sorted.findIndex((s) => s.id === w.id),
      }));
    });
  };

  // Compact 전용 — 4-옵션 사이즈 설정
  const compactSetSize = (id: string, size: CompactSize) => {
    setLayout((prev) => prev.map((w) => (w.id === id ? { ...w, compactSize: size } : w)));
  };

  return (
    <DotModeContext.Provider value={dotMode}>
    <div className={`page ${dotMode.enabled ? "dot-mode" : ""} ${dotMode.enabled && dotMode.scope === "full" ? "dot-mode-full" : ""}`}>
      <PageHeader
        title="홈"
        desc={
          compact
            ? "드래그로 순서 변경, 우하단 4-사이즈 선택, 우측 상단에서 추가/제거"
            : "위젯을 드래그하여 이동, 우하단을 끌어 크기 조절, 우측 상단에서 추가/제거 하세요."
        }
        actions={
        <div className="page-actions">
          {editMode && (
            <>
              <button className="btn-ghost" onClick={onSaveAsDefault} title="현재 배치를 기본 배치로 저장">
                <Icon name="backup" size={14} /> 기본으로 저장
              </button>
              <button className="btn-ghost" onClick={onReset} title="기본 배치로 복원">
                <Icon name="undo" size={14} /> 기본 배치
              </button>
              {user?.isAdmin && (
                <button className="btn-ghost" onClick={() => setRoleLayoutOpen(true)} title="현재 배치를 부서(그룹) 기본 배치로 저장하고 구성원에게 적용">
                  <Icon name="users" size={14} /> 부서 기본 배치
                </button>
              )}
              <div className="add-widget-wrap">
                <button
                  className={`btn-ghost ${dotMenuOpen ? "is-on" : ""} ${dotMode.enabled ? "is-active" : ""}`}
                  onClick={() => setDotMenuOpen((o) => !o)}
                  title="차트를 픽셀아트 스타일로"
                >
                  <Icon name="cube" size={14} /> 도트 모드{dotMode.enabled ? " ●" : ""}
                </button>
                <DotModeMenu
                  open={dotMenuOpen}
                  onClose={() => setDotMenuOpen(false)}
                  value={dotMode}
                  onChange={setDotMode}
                />
              </div>
            </>
          )}
          {editMode && (
            <div className="add-widget-wrap">
              <button className={`btn-ghost ${addOpen ? "is-on" : ""}`} onClick={() => setAddOpen((o) => !o)}>
                <Icon name="plus" size={14} /> 위젯 추가
              </button>
              <AddWidgetMenu open={addOpen} onClose={() => setAddOpen(false)} layout={layout} hidden={roleHidden} onAdd={onAdd} />
            </div>
          )}
          <button className={`btn-primary ${editMode ? "is-on" : ""}`} onClick={() => { setAddOpen(false); setEditMode((e) => !e); }}>
            {editMode ? <><Icon name="close" size={14} /> 편집 완료</> : <><Icon name="pref" size={14} /> 편집</>}
          </button>
        </div>
        }
      />

      {roleLayoutOpen && (
        <RoleLayoutModal unit={unit} layout={layout as unknown as Record<string, unknown>[]} onClose={() => setRoleLayoutOpen(false)} />
      )}

      <div
        ref={gridRef}
        className={`widget-grid ${editMode ? "is-edit" : ""}`}
        style={{ gridTemplateRows: `repeat(${totalRows}, ${ROW_H}px)` }}
      >
        {editMode && !compact && (
          <div className="grid-overlay" aria-hidden="true">
            {Array.from({ length: 12 * totalRows }).map((_, i) => (
              <div key={i} className="grid-cell" />
            ))}
          </div>
        )}
        {renderOrder.map((w) => (
          <WidgetTile
            key={w.id}
            widget={w}
            editMode={editMode}
            compact={compact}
            colW={colW}
            rowH={ROW_H}
            onMove={onMove}
            onResize={onResize}
            onRemove={onRemove}
            onSettings={setSettingsId}
            onCompactReorder={compactReorder}
            onCompactSetSize={compactSetSize}
            dragging={dragging}
            setDragging={setDragging}
          />
        ))}
        {shownLayout.length === 0 && (
          <div className="grid-empty">
            <Icon name="cube" size={40} style={{ opacity: 0.3 }} />
            <div>표시할 위젯이 없습니다 — 우측 상단 {editMode ? <strong>위젯 추가</strong> : <><strong>편집</strong> → <strong>위젯 추가</strong></>}에서 추가하세요.</div>
          </div>
        )}
      </div>
      {settingsId && (() => {
        const w = layout.find((x) => x.id === settingsId);
        if (!w) return null;
        const taxonomy =
          w.type === "line" ? buildLineChartTaxonomy(matCats) :
          (w.type === "bar" || w.type === "bar-combo") ? buildShipmentTaxonomy() :
          undefined;
        // 라이브 프리뷰 — draft settings로 실제 wrapper 렌더링
        // .widget-chart로 감싸야 chart-fill { flex: 1 } 적용됨 (chart가 frame 가득 채움)
        const renderPreview = (draft: WidgetSettings) => {
          const inner =
            w.type === "line" ? <LineChart settings={draft} /> :
            w.type === "bar"  ? <BarChartShipments settings={draft} /> :
            w.type === "bar-combo" ? <BarChartComboShipments settings={draft} /> :
            null;
          return inner ? <div className="widget-chart">{inner}</div> : null;
        };
        return (
          <WidgetSettingsModal
            key={w.id}
            open
            onClose={() => setSettingsId(null)}
            widgetType={w.type}
            widgetLabel={widgetLabel(w)}
            kpiIndex={w.kpiIndex}
            settings={w.settings ?? {}}
            taxonomy={taxonomy}
            priceSpecs={priceSpecs}
            renderPreview={["line", "bar", "bar-combo"].includes(w.type) ? renderPreview : undefined}
            onSave={(s) => {
              setLayout((prev) => prev.map((x) => (x.id === w.id ? { ...x, settings: s } : x)));
            }}
          />
        );
      })()}
    </div>
    </DotModeContext.Provider>
  );
}
